import React, { useState, useEffect, useMemo, useCallback } from "react";
import { SimpleMarkdown } from "../../../../../../util/markdownRenderer";
import { useSelector } from "react-redux";
import { MotifSelect, MotifOption } from "@ey-xd/motif-react";
import "./UpdateSingleFieldAICard.scss";
import aiChatlogo from "../../../../../../assets/Images/aiChatlogo.png";
import { Iconi } from "../../../../../../assets/Icons/Iconi";
import { InfoCircleIcon } from "../../../../../../assets/Icons/InfoCircleIcon";
import { OrdersService } from "../../../../../../api/services/orders.service";
import { LookupService } from "../../../../../../api/services/lookup.service";
import { getApiErrorMessages } from "../../../../../../api/utils/errorHandler";
import { formatDateForInput } from "../../../../../../util/dateUtil";
import ActiveDirectoryUserSearch from "../../../../../OrdersDetails/ActiveDirectoryUserSearch";
import {
  getFieldValidation,
  //isValidDateFormatValue,
  isValidDateValue,
  validateContributorUniqueness,
  validateDateRelationships,
  isContributorField,
  isDateRelationshipField,
} from "../../../../../OrdersDetails/fieldValidations.config";

const isAdditionalDidField = (value) =>
  String(value || "")
    .toLowerCase()
    .includes("additional did");

const normalizeListDisplayValue = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => String(item || "").trim()).filter(Boolean);
  }

  const normalizedValue = String(value || "").trim();

  if (!normalizedValue) {
    return [];
  }

  if (normalizedValue.startsWith("[") && normalizedValue.endsWith("]")) {
    try {
      const parsedValue = JSON.parse(normalizedValue);

      if (Array.isArray(parsedValue)) {
        return parsedValue
          .map((item) => String(item || "").trim())
          .filter(Boolean);
      }
    } catch {
      // Fall back to comma-separated parsing below.
    }
  }

  if (normalizedValue.includes(",")) {
    return normalizedValue
      .split(",")
      .map((item) => item.replace(/^[\s\[\]"]+|[\s\[\]"]+$/g, ""))
      .filter(Boolean);
  }

  return [normalizedValue];
};

const isEmptyFieldValue = (value) => {
  if (Array.isArray(value)) {
    return value.every((item) => String(item ?? "").trim() === "");
  }

  return String(value ?? "").trim() === "";
};

const getInputTooltipTitle = (value) => {
  if (isEmptyFieldValue(value)) {
    return undefined;
  }

  return String(value);
};

const getDropdownOptionValue = (option) => {
  if (typeof option !== "object" || option === null) {
    return option;
  }
  return option.field ?? option.value ?? option.id ?? option.key ?? "";
};

const getDropdownOptionLabel = (option) => {
  if (typeof option !== "object" || option === null) {
    return option;
  }

  return (
    option.field ??
    option.label ??
    option.value ??
    option.name ??
    option.text ??
    ""
  );
};

const isOtherLikeReasonValue = (value) => {
  const normalizedValue = String(value ?? "")
    .trim()
    .toLowerCase();

  return normalizedValue === "other" || normalizedValue === "others";
};

const TOOL_ISSUE_DESCRIPTION_TRIGGER_OPTION =
  "other, including sector tools (specify in comments)";
const REPROCESS_DESCRIPTION_TRIGGER_OPTION =
  "other (fill in reprocess description)";
const ANALYZER_TYPE_FIELD_NAMES = new Set(["analyzertype", "helixanalyzerid"]);
const MARKET_SEGMENT_FIELD_NAMES = new Set([
  "marketsegment",
  "marketsegmentid",
]);

// Location hierarchy field candidates for Country/Office reset logic
const LOCATION_LOOKUP_FIELD_CANDIDATES = {
  area: ["RegionId", "Area"],
  superRegion: ["SuperRegionId", "SuperRegion", "Super Region"],
  country: ["CountryId", "Country", "Region/Country", "Region Country"],
  office: ["OfficeLocation", "Office Location", "Office", "OfficeId"],
};

const getResolvedDropdownOptions = (field) => {
  if (
    Array.isArray(field?.meta?.dropdown_options) &&
    field.meta.dropdown_options.length > 0
  ) {
    return field.meta.dropdown_options;
  }

  return getDropdownOptions(field);
};

const isOtherLikeDropdownSelection = (field, value) => {
  const normalizedValue = String(value ?? "").trim();

  if (!normalizedValue) {
    return false;
  }

  const matchingOption = getResolvedDropdownOptions(field).find((option) =>
    doesDropdownOptionMatchValue(option, normalizedValue),
  );
  const selectedLabel = matchingOption
    ? getDropdownOptionLabel(matchingOption)
    : normalizedValue;

  return (
    isOtherLikeReasonValue(selectedLabel) ||
    isOtherLikeReasonValue(normalizedValue)
  );
};

const isOtherErpUiField = (field) => {
  const normalizedFieldName = normalizeFieldIdentifier(
    field?.meta?.backend_field || field?.field_name,
  );
  const normalizedLabel = normalizeFieldIdentifier(
    field?.meta?.display_label || field?.field_name,
  );

  return (
    normalizedFieldName.includes("othererpname") ||
    normalizedFieldName.includes("erpotherdescription") ||
    normalizedLabel.includes("other(erp)") ||
    normalizedLabel.includes("othererp")
  );
};

const isOtherOnHoldReasonUiField = (field) => {
  const normalizedFieldName = normalizeFieldIdentifier(
    field?.meta?.backend_field || field?.field_name,
  );
  const normalizedLabel = normalizeFieldIdentifier(
    field?.meta?.display_label || field?.field_name,
  );

  return (
    normalizedFieldName.includes("otheronholdreason") ||
    normalizedLabel.includes("otheronholdreason")
  );
};

const isOnHoldReasonUiField = (field) => {
  const normalizedFieldName = normalizeFieldIdentifier(
    field?.meta?.backend_field || field?.field_name,
  );
  const normalizedLabel = normalizeFieldIdentifier(
    field?.meta?.display_label || field?.field_name,
  );

  return (
    (normalizedFieldName.includes("onholdreason") ||
      normalizedLabel.includes("onholdreason")) &&
    !isOtherOnHoldReasonUiField(field)
  );
};

const isOtherCancellationReasonUiField = (field) => {
  const normalizedFieldName = normalizeFieldIdentifier(
    field?.meta?.backend_field || field?.field_name,
  );
  const normalizedLabel = normalizeFieldIdentifier(
    field?.meta?.display_label || field?.field_name,
  );

  return (
    normalizedFieldName.includes("othercancellationreason") ||
    normalizedLabel.includes("othercancellationreason")
  );
};

const isCancellationReasonUiField = (field) => {
  const normalizedFieldName = normalizeFieldIdentifier(
    field?.meta?.backend_field || field?.field_name,
  );
  const normalizedLabel = normalizeFieldIdentifier(
    field?.meta?.display_label || field?.field_name,
  );

  return (
    (normalizedFieldName.includes("cancellationreason") ||
      normalizedLabel.includes("cancellationreason")) &&
    !isOtherCancellationReasonUiField(field)
  );
};

const isOtherCleansingToolUiField = (field) => {
  const normalizedFieldName = normalizeFieldIdentifier(
    field?.meta?.backend_field || field?.field_name,
  );
  const normalizedLabel = normalizeFieldIdentifier(
    field?.meta?.display_label || field?.field_name,
  );

  return (
    normalizedFieldName.includes("othercleansingtool") ||
    normalizedLabel.includes("othercleansingtool")
  );
};

const isOtherAnalyzerTypeUiField = (field) => {
  const normalizedFieldName = normalizeFieldIdentifier(
    field?.meta?.backend_field || field?.field_name,
  );
  const normalizedLabel = normalizeFieldIdentifier(
    field?.meta?.display_label || field?.field_name,
  );

  const hasOtherAnalyzerTypeTokens = (value) => {
    return (
      value.includes("other") &&
      (value.includes("analyzer") || value.includes("helixanalyzer")) &&
      (value.includes("type") || value.includes("helix"))
    );
  };

  return (
    normalizedFieldName.includes("otheranalyzertype") ||
    normalizedFieldName.includes("otherhelixanalyzer") ||
    normalizedLabel.includes("otheranalyzertype") ||
    hasOtherAnalyzerTypeTokens(normalizedFieldName) ||
    hasOtherAnalyzerTypeTokens(normalizedLabel)
  );
};

const isOtherMarketSegmentUiField = (field) => {
  const normalizedFieldName = normalizeFieldIdentifier(
    field?.meta?.backend_field || field?.field_name,
  );
  const normalizedLabel = normalizeFieldIdentifier(
    field?.meta?.display_label || field?.field_name,
  );

  return (
    normalizedFieldName.includes("othermarketsegment") ||
    normalizedLabel.includes("othermarketsegment")
  );
};

const isCleansingToolUiField = (field) => {
  const normalizedFieldName = normalizeFieldIdentifier(
    field?.meta?.backend_field || field?.field_name,
  );
  const normalizedLabel = normalizeFieldIdentifier(
    field?.meta?.display_label || field?.field_name,
  );

  return (
    (normalizedFieldName.includes("cleansingtool") ||
      normalizedLabel.includes("cleansingtool")) &&
    !isOtherCleansingToolUiField(field)
  );
};

const isAnalyzerTypeUiField = (field) => {
  const normalizedFieldName = normalizeFieldIdentifier(
    field?.meta?.backend_field || field?.field_name,
  );
  const normalizedLabel = normalizeFieldIdentifier(
    field?.meta?.display_label || field?.field_name,
  );

  return (
    (ANALYZER_TYPE_FIELD_NAMES.has(normalizedFieldName) ||
      normalizedLabel.includes("analyzertype") ||
      (normalizedLabel.includes("analyzer") &&
        normalizedLabel.includes("type"))) &&
    !isOtherAnalyzerTypeUiField(field)
  );
};

const isMarketSegmentUiField = (field) => {
  const normalizedFieldName = normalizeFieldIdentifier(
    field?.meta?.backend_field || field?.field_name,
  );
  const normalizedLabel = normalizeFieldIdentifier(
    field?.meta?.display_label || field?.field_name,
  );

  return (
    (MARKET_SEGMENT_FIELD_NAMES.has(normalizedFieldName) ||
      normalizedLabel.includes("marketsegment")) &&
    !isOtherMarketSegmentUiField(field)
  );
};

const isErpNameUiField = (field) => {
  const normalizedFieldName = normalizeFieldIdentifier(
    field?.meta?.backend_field || field?.field_name,
  );
  const normalizedLabel = normalizeFieldIdentifier(
    field?.meta?.display_label || field?.field_name,
  );

  return (
    (normalizedFieldName.includes("erpname") ||
      normalizedFieldName.includes("datasourceerpid") ||
      normalizedLabel.includes("erpname")) &&
    !isOtherErpUiField(field)
  );
};

const isOtherExtractionToolUiField = (field) => {
  const normalizedFieldName = normalizeFieldIdentifier(
    field?.meta?.backend_field || field?.field_name,
  );
  const normalizedLabel = normalizeFieldIdentifier(
    field?.meta?.display_label || field?.field_name,
  );

  return (
    normalizedFieldName.includes("otherextractiontool") ||
    normalizedFieldName.includes("extractiontoolothers") ||
    normalizedLabel.includes("otherextractiontool") ||
    normalizedLabel.includes("extractiontoolothers")
  );
};

const isExtractionToolUiField = (field) => {
  const normalizedFieldName = normalizeFieldIdentifier(
    field?.meta?.backend_field || field?.field_name,
  );
  const normalizedLabel = normalizeFieldIdentifier(
    field?.meta?.display_label || field?.field_name,
  );

  return (
    (normalizedFieldName.includes("extractiontool") ||
      normalizedLabel.includes("extractiontool")) &&
    !isOtherExtractionToolUiField(field)
  );
};

const isToolIssueDescriptionUiField = (field) => {
  const normalizedFieldName = normalizeFieldIdentifier(
    field?.meta?.backend_field || field?.field_name,
  );
  const normalizedLabel = normalizeFieldIdentifier(
    field?.meta?.display_label || field?.field_name,
  );

  return (
    normalizedFieldName.includes("toolissuedescription") ||
    normalizedLabel.includes("toolissuedescription")
  );
};

const isToolIssueTypeUiField = (field) => {
  const normalizedFieldName = normalizeFieldIdentifier(
    field?.meta?.backend_field || field?.field_name,
  );
  const normalizedLabel = normalizeFieldIdentifier(
    field?.meta?.display_label || field?.field_name,
  );

  return (
    (normalizedFieldName.includes("toolissuetype") ||
      normalizedLabel.includes("toolissuetype")) &&
    !isToolIssueDescriptionUiField(field)
  );
};

const isToolIssueDescriptionTriggerValue = (value) => {
  const normalizedValue = String(value ?? "")
    .trim()
    .toLowerCase();

  return (
    normalizedValue === "other" ||
    normalizedValue === TOOL_ISSUE_DESCRIPTION_TRIGGER_OPTION
  );
};

const isToolIssueDescriptionSelection = (field, value) => {
  const normalizedValue = String(value ?? "").trim();

  if (!normalizedValue) {
    return false;
  }

  const matchingOption = getResolvedDropdownOptions(field).find((option) =>
    doesDropdownOptionMatchValue(option, normalizedValue),
  );
  const selectedLabel = matchingOption
    ? getDropdownOptionLabel(matchingOption)
    : normalizedValue;

  return (
    isToolIssueDescriptionTriggerValue(selectedLabel) ||
    isToolIssueDescriptionTriggerValue(normalizedValue)
  );
};

const isReprocessDescriptionUiField = (field) => {
  const normalizedFieldName = normalizeFieldIdentifier(
    field?.meta?.backend_field || field?.field_name,
  );
  const normalizedLabel = normalizeFieldIdentifier(
    field?.meta?.display_label || field?.field_name,
  );

  return (
    normalizedFieldName.includes("reprocessdescription") ||
    normalizedLabel.includes("reprocessdescription")
  );
};

const isReprocessReasonUiField = (field) => {
  const normalizedFieldName = normalizeFieldIdentifier(
    field?.meta?.backend_field || field?.field_name,
  );
  const normalizedLabel = normalizeFieldIdentifier(
    field?.meta?.display_label || field?.field_name,
  );

  return (
    (normalizedFieldName.includes("reprocessreason") ||
      normalizedLabel.includes("reprocessreason")) &&
    !isReprocessDescriptionUiField(field)
  );
};

const isReprocessDescriptionTriggerValue = (value) => {
  const normalizedValue = String(value ?? "")
    .trim()
    .toLowerCase();

  return (
    normalizedValue === "other" ||
    normalizedValue === REPROCESS_DESCRIPTION_TRIGGER_OPTION
  );
};

const isReprocessDescriptionSelection = (field, value) => {
  const normalizedValue = String(value ?? "").trim();

  if (!normalizedValue) {
    return false;
  }

  const matchingOption = getResolvedDropdownOptions(field).find((option) =>
    doesDropdownOptionMatchValue(option, normalizedValue),
  );
  const selectedLabel = matchingOption
    ? getDropdownOptionLabel(matchingOption)
    : normalizedValue;

  return (
    isReprocessDescriptionTriggerValue(selectedLabel) ||
    isReprocessDescriptionTriggerValue(normalizedValue)
  );
};

const doesDropdownOptionMatchValue = (option, value) => {
  const normalizedValue = String(value || "")
    .trim()
    .toLowerCase();

  if (!normalizedValue) {
    return false;
  }

  const optionEntries = [
    getDropdownOptionValue(option),
    getDropdownOptionLabel(option),
    typeof option === "object" && option !== null ? option.rawValue : "",
  ];

  return optionEntries.some(
    (optionEntry) =>
      String(optionEntry || "")
        .trim()
        .toLowerCase() === normalizedValue,
  );
};

const getDropdownOptions = (field) => {
  if (Array.isArray(field?.fieldValueList) && field.fieldValueList.length > 0) {
    return field.fieldValueList;
  }

  if (Array.isArray(field?.fieldValue)) {
    return field.fieldValue;
  }

  if (typeof field?.fieldValue === "string" && field.fieldValue.trim()) {
    try {
      const parsed = JSON.parse(field.fieldValue);
      if (Array.isArray(parsed)) {
        return parsed;
      }
    } catch {
      if (field.fieldValue.includes(",")) {
        return field.fieldValue
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean);
      }
    }
  }

  if (Array.isArray(field?.listOfValues)) {
    return field.listOfValues;
  }

  if (Array.isArray(field?.options)) {
    return field.options;
  }

  return [];
};

const getKeyBackedDropdownOptions = (options) => {
  if (!Array.isArray(options)) {
    return [];
  }

  return options
    .map((option) => {
      const rawValue =
        typeof option === "object" && option !== null
          ? (option.value ?? option.id ?? option.key ?? option.label ?? "")
          : option;
      const key =
        typeof option === "object" && option !== null
          ? (option.key ?? option.field ?? option.label ?? option.value ?? "")
          : option;

      if (String(key ?? "").trim() === "") {
        return null;
      }

      return {
        value: key,
        field: key,
        rawValue,
        isDisabled: Boolean(option?.isDisabled),
      };
    })
    .filter(Boolean);
};

const normalizeFieldIdentifier = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "");

const APPLICATION_USED_LOOKUP_REGION_FIELD_CANDIDATES = [
  "RegionId",
  "Area",
  "Region",
];

const normalizeLookupMatchValue = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase();

const getApplicationUsedHoursReasonRegions = (response) => {
  if (Array.isArray(response)) {
    return response;
  }

  return Array.isArray(response?.data) ? response.data : [];
};
//Coplot Suggestion Change if need to Revert back to previous version

const getApplicationUsedHoursReasonRegionMatchValues = (region) => {
  return [region?.regionId, region?.regionGuid, region?.regionName]
    .map(normalizeLookupMatchValue)
    .filter(Boolean);
};

const getApplicationUsedHoursReasonMatchValues = (applicationUsed) => {
  return [applicationUsed?.applicationUsedId, applicationUsed?.applicationUsed]
    .map(normalizeLookupMatchValue)
    .filter(Boolean);
};

const buildApplicationUsedHoursReasonEntries = (regions = []) => {
  return regions.flatMap((region) =>
    (Array.isArray(region?.applicationUsedData)
      ? region.applicationUsedData
      : []
    ).map((applicationUsed) => ({
      ...applicationUsed,
      regionId: applicationUsed?.regionId ?? region?.regionId,
      regionGuid: region?.regionGuid,
      regionName: region?.regionName,
    })),
  );
};

const buildSavingReasonOptionsFromLookup = (applicationUsed) => {
  if (!Array.isArray(applicationUsed?.savingHoursReason)) {
    return [];
  }

  return applicationUsed.savingHoursReason
    .filter((option) => option?.id != null && option?.reason)
    .map((option) => ({
      value: String(option.id),
      field: option.reason,
      isDisabled: false,
    }));
};

const formatSavingDefaultHoursValue = (value) => {
  if (value === undefined || value === null) {
    return "";
  }

  return String(value).trim();
};

const getOrderDetailsFieldValueByCandidates = (
  orderDetails,
  candidates = [],
) => {
  const normalizedCandidates = candidates
    .map(normalizeFieldIdentifier)
    .filter(Boolean);

  if (!Array.isArray(orderDetails) || normalizedCandidates.length === 0) {
    return "";
  }

  for (const section of orderDetails) {
    const pagefields = Array.isArray(section?.pagefields)
      ? section.pagefields
      : [];

    const matchingField = pagefields.find((field) => {
      const normalizedFieldName = normalizeFieldIdentifier(field?.field);
      const normalizedLabel = normalizeFieldIdentifier(field?.labelToShowOnUI);

      return normalizedCandidates.some(
        (candidate) =>
          candidate === normalizedFieldName || candidate === normalizedLabel,
      );
    });

    if (!matchingField) {
      continue;
    }

    return matchingField?.fieldValue ?? "";
  }

  return "";
};

const FIELD_STATE_KEY_SEPARATOR = "::";
const EFFORT_SECTION_NAME = "Other/Effort";
const SAVING_SECTION_NAME = "Other/Saving";
const REGIONAL_LEAD_READ_ONLY_FIELD_NAMES = [
  "MercuryClientId",
  "ClientName",
  "Manager",
  "EngagementPartner",
];
const EFFORT_FIELD_IDENTIFIERS = new Set([
  "effortforid",
  "efforthours",
  "role",
  "resourcename",
  "reason",
  "effortformasterid",
]);
const SAVING_FIELD_IDENTIFIERS = new Set([
  "hourstosave",
  "applicationusedid",
  "otherreason",
  "reasonid",
  "username",
  "userName",
]);
const INVALID_DATE_MESSAGE = "Enter a valid date";
const INVALID_DROPDOWN_OPTION_MESSAGE =
  "The existing value do not match with list of options. Choose valid option from Dropdown list";
const NON_EDITABLE_FIELD_MESSAGE = "This field is non editable.";
const VALIDATION_WARNINGS_ERROR_MESSAGE = "Validation warnings found:";
const DEFAULT_UPDATE_FIELDS_ERROR_MESSAGE =
  "Failed to update fields. Please try again.";
const CONTRIBUTOR_ROLE_FIELD_MAP = {
  reviewer: "ReviewerID",
  preparer: "PreparerID",
  approver: "ApproverID",
};
const GENERATED_UI_FIELD_EXCLUSIONS = new Set(["effortforid", "id"]);
const GENERATED_EMAIL_FIELDS = new Set(["resourcename", "username"]);
const GENERATED_NUMERIC_FIELDS = new Set(["efforthours", "hourstosave"]);
const GENERATED_TEXTAREA_FIELDS = new Set(["reason", "otherreason"]);
const EFFORT_FIELD_LAYOUT_ORDER = [
  ["ResourceName", "Resource Name"],
  ["Role"],
  ["EffortHours", "Effort Hours", "Effort In Hour", "Efforts In Hours"],
  ["EffortForMasterId", "EffortFor", "Effort For", "Efforts For"],
  ["Reason"],
];
const SAVING_FIELD_LAYOUT_ORDER = [
  ["ApplicationUsedId", "Application Used"],
  ["HoursToSave", "Hours To Save", "Hours Saved"],
  ["UserName", "Username", "User Name"],
  ["ReasonId", "Reason"],
  ["OtherReason", "Other Reason"],
];

const VALIDATION_FIELD_LAYOUT_ORDER = [
  ...EFFORT_FIELD_LAYOUT_ORDER,
  ...SAVING_FIELD_LAYOUT_ORDER,
];

const VALIDATION_FIELD_LOOKUP = VALIDATION_FIELD_LAYOUT_ORDER.reduce(
  (lookup, aliases) => {
    const canonicalFieldKey = aliases[0];

    aliases.forEach((alias) => {
      lookup[normalizeFieldIdentifier(alias)] = canonicalFieldKey;
    });

    return lookup;
  },
  {},
);

const resolveValidationFieldKey = (fieldKey, displayLabel) => {
  const normalizedFieldKey = normalizeFieldIdentifier(fieldKey);
  const normalizedDisplayLabel = normalizeFieldIdentifier(displayLabel);

  return (
    VALIDATION_FIELD_LOOKUP[normalizedFieldKey] ||
    VALIDATION_FIELD_LOOKUP[normalizedDisplayLabel] ||
    fieldKey ||
    displayLabel ||
    ""
  );
};

const isManuallyEditableAutoPopulatedSectionField = (orderField) => {
  const normalizedFieldName = normalizeFieldIdentifier(
    orderField?.field || orderField?.baseFieldName,
  );
  const sectionName = orderField?.sectionName || "";

  if (sectionName === EFFORT_SECTION_NAME) {
    return normalizedFieldName === "resourcename";
  }

  if (sectionName === SAVING_SECTION_NAME) {
    return (
      normalizedFieldName === "hourstosave" ||
      normalizedFieldName === "username"
    );
  }

  return false;
};

const buildSectionRowKey = (sectionName, rowId, rowIndex) => {
  const resolvedRowId =
    rowId === undefined || rowId === null || rowId === ""
      ? String(rowIndex)
      : String(rowId);

  return `${sectionName}${FIELD_STATE_KEY_SEPARATOR}${resolvedRowId}`;
};

const getStringIdentifierValue = (value, fallbackValue = "") =>
  value === undefined || value === null || value === ""
    ? fallbackValue
    : String(value);

const buildFieldStateKey = (
  sectionName,
  rowId,
  rowIndex,
  fieldName,
  hasRows,
) => {
  if (!hasRows) {
    return String(fieldName || "");
  }

  return `${buildSectionRowKey(sectionName, rowId, rowIndex)}${FIELD_STATE_KEY_SEPARATOR}${fieldName}`;
};

const doesFieldMatchCandidates = (field, candidates = []) => {
  const normalizedCandidates = candidates
    .map(normalizeFieldIdentifier)
    .filter(Boolean);

  if (normalizedCandidates.length === 0) {
    return false;
  }

  const normalizedFieldName = normalizeFieldIdentifier(field?.field);
  const normalizedLabel = normalizeFieldIdentifier(field?.labelToShowOnUI);

  return normalizedCandidates.some(
    (candidate) =>
      candidate === normalizedFieldName || candidate === normalizedLabel,
  );
};

const getRepeatedRowFields = (row) => {
  if (Array.isArray(row?.fields)) {
    return row.fields;
  }

  if (Array.isArray(row?.pagefieldsListInfo)) {
    return row.pagefieldsListInfo;
  }

  if (Array.isArray(row?.pagefieldslistinfo)) {
    return row.pagefieldslistinfo;
  }

  if (Array.isArray(row)) {
    return row;
  }

  return [];
};

const getRowDataValueByCandidates = (
  rowDataInfo,
  candidates = [],
  fallbackValue = "",
) => {
  if (!rowDataInfo || typeof rowDataInfo !== "object") {
    return fallbackValue;
  }

  const normalizedCandidates = candidates
    .map(normalizeFieldIdentifier)
    .filter(Boolean);

  if (normalizedCandidates.length === 0) {
    return fallbackValue;
  }

  const matchingKey = Object.keys(rowDataInfo).find((key) =>
    normalizedCandidates.includes(normalizeFieldIdentifier(key)),
  );

  return matchingKey
    ? (rowDataInfo[matchingKey] ?? fallbackValue)
    : fallbackValue;
};

const doesDropdownValueMatchOptions = (field, value) => {
  const normalizedValue = String(value ?? "").trim();

  if (!normalizedValue) {
    return true;
  }

  const options = getDropdownOptions(field);

  if (options.length === 0) {
    return true;
  }

  return options.some((option) =>
    doesDropdownOptionMatchValue(option, normalizedValue),
  );
};

const getDropdownDisplayMatchValue = (field, value) => {
  const normalizedValue = String(value ?? "").trim();

  if (!normalizedValue) {
    return "";
  }

  const matchingOption = getDropdownOptions(field).find((option) =>
    doesDropdownOptionMatchValue(option, normalizedValue),
  );

  return matchingOption
    ? getDropdownOptionLabel(matchingOption)
    : normalizedValue;
};

const getDropdownOptionMatchValues = (option) => {
  if (typeof option !== "object" || option === null) {
    const normalizedValue = normalizeLookupMatchValue(option);

    return normalizedValue ? [normalizedValue] : [];
  }

  return [
    option.rawValue,
    option.value,
    option.id,
    option.key,
    option.field,
    option.label,
    option.name,
    option.text,
  ]
    .map(normalizeLookupMatchValue)
    .filter(Boolean);
};

const findDropdownOptionByMatchValue = (field, value) => {
  const normalizedValue = normalizeLookupMatchValue(value);

  if (!normalizedValue) {
    return null;
  }

  return (
    getDropdownOptions(field).find((option) =>
      getDropdownOptionMatchValues(option).includes(normalizedValue),
    ) || null
  );
};

const getApplicationUsedDropdownSelectValue = (field, value) => {
  const normalizedValue = String(value ?? "").trim();

  if (!normalizedValue) {
    return "";
  }

  const matchingOption = findDropdownOptionByMatchValue(field, normalizedValue);

  return matchingOption
    ? String(getDropdownOptionValue(matchingOption) ?? "").trim()
    : normalizedValue;
};

const getApplicationUsedDropdownDisplayValue = (field, value) => {
  const normalizedValue = String(value ?? "").trim();

  if (!normalizedValue) {
    return "";
  }

  const matchingOption = findDropdownOptionByMatchValue(field, normalizedValue);

  return matchingOption
    ? String(getDropdownOptionLabel(matchingOption) ?? "").trim()
    : normalizedValue;
};

const getEffortForDropdownSelectValue = (field, value) => {
  const normalizedValue = String(value ?? "").trim();

  if (!normalizedValue) {
    return "";
  }

  const matchingOption = findDropdownOptionByMatchValue(field, normalizedValue);

  return matchingOption
    ? String(getDropdownOptionValue(matchingOption) ?? "").trim()
    : normalizedValue;
};

const getEffortForDropdownDisplayValue = (field, value) => {
  const normalizedValue = String(value ?? "").trim();

  if (!normalizedValue) {
    return "";
  }

  const matchingOption = findDropdownOptionByMatchValue(field, normalizedValue);

  return matchingOption
    ? String(getDropdownOptionLabel(matchingOption) ?? "").trim()
    : normalizedValue;
};

const getDerivedFieldLayoutOrder = (sectionName) =>
  sectionName === EFFORT_SECTION_NAME
    ? EFFORT_FIELD_LAYOUT_ORDER
    : SAVING_FIELD_LAYOUT_ORDER;

const getUpdateOrderValidationWarnings = (response) => {
  return Array.from(
    new Set(
      [response?.validationWarnings, response?.data?.validationWarnings]
        .flatMap((warnings) => (Array.isArray(warnings) ? warnings : []))
        .map((warning) => String(warning ?? "").trim())
        .filter(Boolean),
    ),
  );
};

const getApiErrorResponseData = (error) => {
  return error?.responseData || error?.response?.data || {};
};

const getApiErrorDisplayMessage = (error, fallbackMessage) => {
  const responseData = getApiErrorResponseData(error);

  return (
    error?.message ||
    responseData?.message ||
    responseData?.Message ||
    fallbackMessage
  );
};

const getOrderedDerivedRowFields = (sectionName, fields = []) => {
  const layoutPriorities = getDerivedFieldLayoutOrder(sectionName);

  return fields
    .map((field, index) => ({ field, index }))
    .filter(
      ({ field }) =>
        !GENERATED_UI_FIELD_EXCLUSIONS.has(
          normalizeFieldIdentifier(field?.field || field?.labelToShowOnUI),
        ),
    )
    .sort((left, right) => {
      const leftPriority = layoutPriorities.findIndex((candidates) =>
        doesFieldMatchCandidates(left.field, candidates),
      );
      const rightPriority = layoutPriorities.findIndex((candidates) =>
        doesFieldMatchCandidates(right.field, candidates),
      );

      const resolvedLeftPriority =
        leftPriority === -1 ? Number.MAX_SAFE_INTEGER : leftPriority;
      const resolvedRightPriority =
        rightPriority === -1 ? Number.MAX_SAFE_INTEGER : rightPriority;

      if (resolvedLeftPriority !== resolvedRightPriority) {
        return resolvedLeftPriority - resolvedRightPriority;
      }

      return left.index - right.index;
    })
    .map(({ field }) => field);
};

const createNormalizedSectionRows = (section) => {
  const hasRepeatedRows =
    Array.isArray(section?.pagefieldsList) && section.pagefieldsList.length > 0;
  const sectionRowData = Array.isArray(section?.rowData) ? section.rowData : [];
  const rawRows = hasRepeatedRows
    ? section.pagefieldsList
    : Array.isArray(section?.pagefields) && section.pagefields.length > 0
      ? [section.pagefields]
      : [];

  return rawRows
    .map((row, rowIndex) => {
      const rowDataInfo =
        row?.rowDataInfo ||
        row?.rowdatainfo ||
        row?.rowData ||
        sectionRowData[rowIndex] ||
        null;
      const pagefieldsListInfo = getRepeatedRowFields(row);
      const resolvedRowId = getStringIdentifierValue(
        row?.rowId ??
          rowDataInfo?.ID ??
          rowDataInfo?.Id ??
          rowDataInfo?.EffortForId ??
          rowDataInfo?.effortForId,
        String(rowIndex),
      );
      const rowKey = buildSectionRowKey(
        section?.section || "",
        resolvedRowId,
        rowIndex,
      );
      const fields = Array.isArray(pagefieldsListInfo)
        ? pagefieldsListInfo.map((field) => ({
            ...field,
            sectionName: section?.section || "",
            rowId: resolvedRowId,
            rowIndex,
            rowKey,
            rowDataInfo,
            dropdownReason:
              field?.dropdownReason ||
              rowDataInfo?.DropDownReason ||
              rowDataInfo?.DropdownReason ||
              row?.DropDownReason ||
              row?.DropdownReason,
            fieldStateKey: buildFieldStateKey(
              section?.section || "",
              resolvedRowId,
              rowIndex,
              field?.field,
              hasRepeatedRows,
            ),
          }))
        : [];

      return {
        rowId: resolvedRowId,
        rowIndex,
        rowKey,
        rowDataInfo,
        fields,
      };
    })
    .filter((row) => row.fields.length > 0);
};

const resolveGeneratedFieldValue = (orderField, fallbackValues = {}) => {
  const fieldValue = orderField?.fieldValue;

  if (
    fieldValue !== undefined &&
    fieldValue !== null &&
    String(fieldValue).trim() !== ""
  ) {
    return fieldValue;
  }

  const fallbackKey = normalizeFieldIdentifier(
    orderField?.field || orderField?.labelToShowOnUI,
  );

  const rowDataValue = getRowDataValueByCandidates(
    orderField?.rowDataInfo,
    [orderField?.field, orderField?.labelToShowOnUI],
    "",
  );

  if (String(rowDataValue || "").trim() !== "") {
    return rowDataValue;
  }

  return fallbackValues[fallbackKey] ?? "";
};

const buildInitialFormValues = (fields = []) => {
  const initialValues = {};

  fields.forEach((field) => {
    initialValues[field.field_name] = field.updatedValue || "";
  });

  return initialValues;
};

const getResolvedGeneratedFieldDefinition = (field) => {
  if (
    field?.sectionName === SAVING_SECTION_NAME &&
    doesFieldMatchCandidates(field, ["ReasonId", "Reason"])
  ) {
    const reasonOptions = getKeyBackedDropdownOptions(field?.dropdownReason);

    if (reasonOptions.length > 0) {
      return {
        ...field,
        dataType: "dropdown",
        fieldValueList: reasonOptions,
      };
    }
  }

  return field;
};

const resolveGeneratedDropdownValue = (field, value) => {
  const normalizedValue = String(value ?? "").trim();

  if (!normalizedValue) {
    return "";
  }

  const matchingOption = getDropdownOptions(field).find((option) =>
    doesDropdownOptionMatchValue(option, normalizedValue),
  );

  return matchingOption
    ? String(getDropdownOptionValue(matchingOption) ?? normalizedValue)
    : normalizedValue;
};

const buildGeneratedUiField = (orderField, fallbackValues = {}) => {
  const resolvedOrderField = getResolvedGeneratedFieldDefinition(orderField);
  const backendField =
    resolvedOrderField?.field || resolvedOrderField?.labelToShowOnUI || "";
  const displayLabel = resolvedOrderField?.labelToShowOnUI || backendField;
  const normalizedBackendField = normalizeFieldIdentifier(backendField);
  const dropdownOptions = getDropdownOptions(resolvedOrderField);
  const isDropdown = dropdownOptions.length > 0;
  const isEmailField = GENERATED_EMAIL_FIELDS.has(normalizedBackendField);
  const valueFormat = isDropdown
    ? "dropdown"
    : GENERATED_NUMERIC_FIELDS.has(normalizedBackendField)
      ? "number"
      : GENERATED_TEXTAREA_FIELDS.has(normalizedBackendField)
        ? "textarea"
        : isEmailField
          ? "email"
          : "text";
  const resolvedUpdatedValue = resolveGeneratedFieldValue(
    resolvedOrderField,
    fallbackValues,
  );

  return {
    field_name: resolvedOrderField?.fieldStateKey || backendField,
    updatedValue: isDropdown
      ? resolveGeneratedDropdownValue(resolvedOrderField, resolvedUpdatedValue)
      : resolvedUpdatedValue,
    meta: {
      display_label: displayLabel,
      backend_field: backendField,
      value_format: valueFormat,
      data_type:
        resolvedOrderField?.dataType || (isDropdown ? "dropdown" : valueFormat),
      required: Boolean(
        resolvedOrderField?.isRequired || resolvedOrderField?.required,
      ),
      dropdown: isDropdown,
      dropdown_options: dropdownOptions,
      row_key: resolvedOrderField?.rowKey || "",
      row_index: resolvedOrderField?.rowIndex ?? 0,
      row_id: resolvedOrderField?.rowId ?? "",
      section_name: resolvedOrderField?.sectionName || "",
    },
  };
};

const buildFlagDrivenUiFields = ({
  sectionRowsBySection = new Map(),
  effortsFlag = false,
  savingsFlag = false,
  fallbackValues = {},
}) => {
  if (!sectionRowsBySection || typeof sectionRowsBySection.get !== "function") {
    return [];
  }

  const targetSections = [];

  if (effortsFlag) {
    targetSections.push(EFFORT_SECTION_NAME);
  }

  if (savingsFlag) {
    targetSections.push(SAVING_SECTION_NAME);
  }

  if (targetSections.length === 0) {
    return [];
  }

  return targetSections.flatMap((sectionName) =>
    (sectionRowsBySection.get(sectionName) || []).flatMap((row) =>
      getOrderedDerivedRowFields(sectionName, row.fields).map((field) =>
        buildGeneratedUiField(field, fallbackValues),
      ),
    ),
  );
};

const padDateSegment = (value) => String(value).padStart(2, "0");

const getDateSegments = (value) => {
  const normalizedValue = String(value ?? "").trim();

  if (!normalizedValue) {
    return null;
  }

  const dateOnly = normalizedValue.includes(" ")
    ? normalizedValue.split(" ")[0]
    : normalizedValue;
  const isoMatch = dateOnly.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);

  if (isoMatch) {
    return {
      year: isoMatch[1],
      month: isoMatch[2],
      day: isoMatch[3],
    };
  }

  const parts = dateOnly.split(/[/-]/);

  if (parts.length === 3 && parts[2].length === 4) {
    const [firstPart, secondPart, year] = parts;
    const firstNumber = Number(firstPart);
    const secondNumber = Number(secondPart);
    const isDayMonthFormat = firstNumber > 12 || secondNumber <= 12;

    return {
      year,
      month: isDayMonthFormat ? secondPart : firstPart,
      day: isDayMonthFormat ? firstPart : secondPart,
    };
  }

  return null;
};

const getDateInputValue = (value) => {
  const normalizedValue = String(value ?? "").trim();

  if (!normalizedValue || !isValidDateValue(normalizedValue)) {
    return "";
  }

  const formattedValue = formatDateForInput(normalizedValue);

  return /^\d{4}-\d{2}-\d{2}$/.test(formattedValue) ? formattedValue : "";
};

const getDateDisplayValue = (value) => {
  const normalizedValue = String(value ?? "").trim();

  if (!normalizedValue) {
    return "";
  }

  const dateSegments = getDateSegments(
    getDateInputValue(normalizedValue) || normalizedValue,
  );

  if (!dateSegments) {
    return normalizedValue;
  }

  return `${padDateSegment(dateSegments.day)}-${padDateSegment(dateSegments.month)}-${dateSegments.year}`;
};

export const UpdateSingleFieldAICard = ({
  message,
  onActionClick,
  orderDetails,
  onRefreshOrderDetails,
}) => {
  // Get user info from Redux store
  const user = useSelector((state) => state.user?.user);
  const selectedRole = useSelector((state) => state.user?.selectedRole);
  const userEmail =
    user?.email ||
    user?.mail ||
    user?.username ||
    user?.preferred_username ||
    "";

  const currentUserRole = useMemo(() => {
    const roleSource =
      selectedRole?.roleName ||
      selectedRole?.name ||
      (typeof selectedRole === "string" ? selectedRole : "") ||
      user?.role ||
      "";
    return String(roleSource).trim();
  }, [selectedRole, user?.role]);

  const normalizedCurrentUserRole = useMemo(
    () => currentUserRole.toUpperCase(),
    [currentUserRole],
  );

  const isDidRole = useMemo(
    () => !normalizedCurrentUserRole || normalizedCurrentUserRole === "DID",
    [normalizedCurrentUserRole],
  );

  const isRegionalLead = useMemo(() => {
    return normalizedCurrentUserRole === "REGIONAL LEAD";
  }, [normalizedCurrentUserRole]);

  const normalizedSectionRowsBySection = useMemo(() => {
    const rowsBySection = new Map();

    for (const section of orderDetails || []) {
      if (
        section?.section === EFFORT_SECTION_NAME ||
        section?.section === SAVING_SECTION_NAME
      ) {
        rowsBySection.set(
          section.section,
          createNormalizedSectionRows(section),
        );
      }
    }

    return rowsBySection;
  }, [orderDetails]);

  const effortSectionRows = useMemo(
    () => normalizedSectionRowsBySection.get(EFFORT_SECTION_NAME) || [],
    [normalizedSectionRowsBySection],
  );

  const savingSectionRows = useMemo(
    () => normalizedSectionRowsBySection.get(SAVING_SECTION_NAME) || [],
    [normalizedSectionRowsBySection],
  );

  const hasEffortSection = effortSectionRows.length > 0;
  const hasSavingSection = savingSectionRows.length > 0;

  const orderFieldDefinitions = useMemo(() => {
    if (!Array.isArray(orderDetails)) {
      return [];
    }

    const nonRepeatedFields = orderDetails.flatMap((section) => {
      if (
        section?.section === EFFORT_SECTION_NAME ||
        section?.section === SAVING_SECTION_NAME
      ) {
        return [];
      }

      return (section?.pagefields || []).map((field) => ({
        ...field,
        sectionName: section?.section || "",
      }));
    });

    const repeatedFields = [...effortSectionRows, ...savingSectionRows].flatMap(
      (row) => row.fields,
    );

    return [...nonRepeatedFields, ...repeatedFields];
  }, [effortSectionRows, orderDetails, savingSectionRows]);

  const isDerivedSectionUiFields = useMemo(
    () =>
      (!Array.isArray(message.ui_fields) || message.ui_fields.length === 0) &&
      (message.efforts_flag === true || message.savings_flag === true),
    [message.efforts_flag, message.savings_flag, message.ui_fields],
  );

  const baseUiFields = useMemo(() => {
    if (Array.isArray(message.ui_fields) && message.ui_fields.length > 0) {
      return message.ui_fields;
    }

    return buildFlagDrivenUiFields({
      sectionRowsBySection: normalizedSectionRowsBySection,
      effortsFlag: message.efforts_flag === true,
      savingsFlag: message.savings_flag === true,
      fallbackValues: {
        username: userEmail,
      },
    });
  }, [
    message.efforts_flag,
    message.savings_flag,
    message.ui_fields,
    normalizedSectionRowsBySection,
    userEmail,
  ]);

  const [formValues, setFormValues] = useState(() =>
    buildInitialFormValues(baseUiFields),
  );
  const [inputValue, setInputValue] = useState("");
  const [fieldValidationErrors, setFieldValidationErrors] = useState({});
  const [
    applicationUsedHoursReasonLookupResponse,
    setApplicationUsedHoursReasonLookupResponse,
  ] = useState(null);
  const [locationHierarchyLookupResponse, setLocationHierarchyLookupResponse] =
    useState(null);

  const findMatchingOrderField = useCallback(
    (backendField, displayLabel = "", rowKey = "") => {
      const normalizedBackendField = normalizeFieldIdentifier(backendField);
      const resolvedBackendField =
        normalizedBackendField === "preparinganalyzerandmemostartdate"
          ? "preparinganalyzerstartdate"
          : normalizedBackendField === "preparinganalyzerandmemoenddate"
            ? "preparinganalyzerenddate"
            : normalizedBackendField;
      const normalizedDisplayLabel = normalizeFieldIdentifier(displayLabel);
      const scopedOrderFields = orderFieldDefinitions.filter(
        (orderField) =>
          !(rowKey && orderField?.rowKey && orderField.rowKey !== rowKey),
      );

      if (
        resolvedBackendField === "preparinganalyzerstartdate" ||
        resolvedBackendField === "preparinganalyzerenddate"
      ) {
        const analyzerFieldMatch = [...scopedOrderFields]
          .reverse()
          .find(
            (orderField) =>
              normalizeFieldIdentifier(orderField?.field) ===
              resolvedBackendField,
          );

        if (analyzerFieldMatch) {
          return analyzerFieldMatch;
        }
      }

      return (
        scopedOrderFields.find((orderField) => {
          return (
            normalizeFieldIdentifier(orderField?.field) ===
              resolvedBackendField ||
            normalizeFieldIdentifier(orderField?.labelToShowOnUI) ===
              normalizedDisplayLabel
          );
        }) || null
      );
    },
    [orderFieldDefinitions],
  );

  useEffect(() => {
    let isCancelled = false;

    if (!hasSavingSection) {
      setApplicationUsedHoursReasonLookupResponse(null);
      return () => {
        isCancelled = true;
      };
    }

    const fetchApplicationUsedHoursReasonLookup = async () => {
      try {
        const response =
          await LookupService.getApplicationUsedHoursReasonData();

        if (isCancelled) {
          return;
        }

        setApplicationUsedHoursReasonLookupResponse(response || null);
      } catch (error) {
        if (isCancelled) {
          return;
        }

        console.error(
          "Failed to fetch application used hours and reason lookup data:",
          error,
        );
        setApplicationUsedHoursReasonLookupResponse(null);
      }
    };

    fetchApplicationUsedHoursReasonLookup();

    return () => {
      isCancelled = true;
    };
  }, [hasSavingSection]);

  // Fetch location hierarchy data for Country/Office dropdown filtering
  useEffect(() => {
    let isCancelled = false;

    const fetchLocationHierarchy = async () => {
      try {
        const response =
          await LookupService.getSupRegCountryOfficeDataByRegion();

        if (isCancelled) {
          return;
        }

        setLocationHierarchyLookupResponse(response || null);
      } catch (error) {
        if (isCancelled) {
          return;
        }

        console.error(
          "Failed to fetch location hierarchy lookup data:",
          error,
        );
        setLocationHierarchyLookupResponse(null);
      }
    };

    fetchLocationHierarchy();

    return () => {
      isCancelled = true;
    };
  }, []);

  // Helper to get location hierarchy records from the API response
  const getLocationHierarchyRecords = useCallback((response) => {
    if (Array.isArray(response)) {
      return response;
    }
    if (response?.data && Array.isArray(response.data)) {
      return response.data;
    }
    return [];
  }, []);

  const locationHierarchyLookupData = useMemo(
    () => getLocationHierarchyRecords(locationHierarchyLookupResponse),
    [getLocationHierarchyRecords, locationHierarchyLookupResponse],
  );

  // Helper to build location dropdown option
  const buildLocationDropdownOption = useCallback(({ value, id, label }) => {
    if (!label && !value && !id) {
      return null;
    }
    return {
      value: value ?? id ?? label,
      id,
      label: label ?? value,
      field: label ?? value ?? id,
    };
  }, []);

  const buildCountryLookupOption = useCallback(
    (country) => {
      return buildLocationDropdownOption({
        value:
          country?.countryGuid ?? country?.countryId ?? country?.countryName,
        id: country?.countryId,
        label: country?.countryName,
      });
    },
    [buildLocationDropdownOption],
  );

  const buildOfficeLookupOption = useCallback(
    (office) => {
      return buildLocationDropdownOption({
        value:
          office?.officeGuid ?? office?.officeId ?? office?.officeLocation,
        id: office?.officeId,
        label: office?.officeLocation,
      });
    },
    [buildLocationDropdownOption],
  );

  const buildRegionLookupOption = useCallback(
    (region) => {
      return buildLocationDropdownOption({
        value: region?.regionGuid ?? region?.regionId ?? region?.regionName,
        id: region?.regionId,
        label: region?.regionName,
      });
    },
    [buildLocationDropdownOption],
  );

  const buildSuperRegionLookupOption = useCallback(
    (superRegion) => {
      return buildLocationDropdownOption({
        value:
          superRegion?.superRegionGuid ??
          superRegion?.superRegionId ??
          superRegion?.superRegionName,
        id: superRegion?.superRegionId,
        label: superRegion?.superRegionName,
      });
    },
    [buildLocationDropdownOption],
  );

  // Helper to normalize location lookup value for comparison
  const normalizeLocationLookupValue = useCallback((value) => {
    return String(value ?? "")
      .trim()
      .toLowerCase();
  }, []);

  // Helper to find matched location record
  const findMatchedLocationRecord = useCallback(
    (records = [], selectedValue, buildOption) => {
      const normalizedSelectedValue = normalizeLocationLookupValue(selectedValue);

      if (!normalizedSelectedValue) {
        return null;
      }

      return (
        records.find((record) => {
          const option = buildOption(record);
          const optionMatchValues =
            typeof option === "object" && option !== null
              ? [
                  option.rawValue,
                  option.value,
                  option.id,
                  option.key,
                  option.field,
                  option.label,
                  option.name,
                  option.text,
                ]
              : [option];
          const recordMatchValues = [
            record?.regionName,
            record?.superRegionName,
            record?.countryName,
            record?.officeLocation,
          ];

          return [...optionMatchValues, ...recordMatchValues]
            .filter(Boolean)
            .some(
              (candidateValue) =>
                normalizeLocationLookupValue(candidateValue) ===
                normalizedSelectedValue,
            );
        }) || null
      );
    },
    [normalizeLocationLookupValue],
  );

  // Helper to check if a field matches location candidates
  const doesFieldMatchLocationCandidates = useCallback(
    (field, candidates = []) => {
      const normalizedFieldName = normalizeFieldIdentifier(
        field?.meta?.backend_field || field?.field_name || field?.field,
      );
      const normalizedLabel = normalizeFieldIdentifier(
        field?.meta?.display_label || field?.field_name || field?.labelToShowOnUI,
      );
      const normalizedCandidates = candidates.map(normalizeFieldIdentifier);

      return normalizedCandidates.some(
        (candidate) =>
          candidate === normalizedFieldName || candidate === normalizedLabel,
      );
    },
    [],
  );

  // Helper to get office options filtered by selected country
  const getOfficeOptionsForCountry = useCallback(
    (selectedCountryValue) => {
      if (!selectedCountryValue || !locationHierarchyLookupData.length) {
        return [];
      }

      // Search through all regions -> superRegions -> countries to find the matching country
      for (const region of locationHierarchyLookupData) {
        for (const superRegion of region?.superRegions || []) {
          const matchedCountry = findMatchedLocationRecord(
            superRegion?.countries || [],
            selectedCountryValue,
            buildCountryLookupOption,
          );

          if (matchedCountry) {
            return (matchedCountry.officelocations || [])
              .map(buildOfficeLookupOption)
              .filter(Boolean);
          }
        }
      }

      return [];
    },
    [
      buildCountryLookupOption,
      buildOfficeLookupOption,
      findMatchedLocationRecord,
      locationHierarchyLookupData,
    ],
  );

  const applicationUsedHoursReasonLookupRegions = useMemo(
    () =>
      getApplicationUsedHoursReasonRegions(
        applicationUsedHoursReasonLookupResponse,
      ),
    [applicationUsedHoursReasonLookupResponse],
  );

  const allApplicationUsedHoursReasonLookupEntries = useMemo(
    () =>
      buildApplicationUsedHoursReasonEntries(
        applicationUsedHoursReasonLookupRegions,
      ),
    [applicationUsedHoursReasonLookupRegions],
  );

  const applicationUsedHoursReasonLookupEntries = useMemo(() => {
    const selectedRegionValue = getOrderDetailsFieldValueByCandidates(
      orderDetails,
      APPLICATION_USED_LOOKUP_REGION_FIELD_CANDIDATES,
    );
    const normalizedSelectedRegionValue =
      normalizeLookupMatchValue(selectedRegionValue);

    if (!normalizedSelectedRegionValue) {
      return allApplicationUsedHoursReasonLookupEntries;
    }

    const matchedRegion =
      applicationUsedHoursReasonLookupRegions.find((region) =>
        getApplicationUsedHoursReasonRegionMatchValues(region).some(
          (candidateValue) => candidateValue === normalizedSelectedRegionValue,
        ),
      ) || null;

    if (!matchedRegion) {
      return allApplicationUsedHoursReasonLookupEntries;
    }

    return buildApplicationUsedHoursReasonEntries([matchedRegion]);
  }, [
    allApplicationUsedHoursReasonLookupEntries,
    applicationUsedHoursReasonLookupRegions,
    orderDetails,
  ]);

  const findMatchedApplicationUsedHoursReasonEntry = useCallback(
    (applicationField, selectedValue) => {
      const normalizedSelectedValue = normalizeLookupMatchValue(selectedValue);

      if (!normalizedSelectedValue) {
        return null;
      }

      const matchCandidates = new Set([normalizedSelectedValue]);

      getDropdownOptions(applicationField).forEach((option) => {
        if (!doesDropdownOptionMatchValue(option, selectedValue)) {
          return;
        }

        getDropdownOptionMatchValues(option).forEach((optionValue) => {
          if (optionValue) {
            matchCandidates.add(optionValue);
          }
        });
      });

      const findMatchingEntry = (entries = []) => {
        return (
          entries.find((applicationUsed) =>
            getApplicationUsedHoursReasonMatchValues(applicationUsed).some(
              (candidateValue) => matchCandidates.has(candidateValue),
            ),
          ) || null
        );
      };

      return (
        findMatchingEntry(applicationUsedHoursReasonLookupEntries) ||
        findMatchingEntry(allApplicationUsedHoursReasonLookupEntries)
      );
    },
    [
      allApplicationUsedHoursReasonLookupEntries,
      applicationUsedHoursReasonLookupEntries,
    ],
  );

  const resolveUiFields = useCallback(
    (candidateFormValues = {}) => {
      return baseUiFields.map((field) => {
        const backendFieldKey = field.meta?.backend_field || field.field_name;
        const displayLabel = field.meta?.display_label || field.field_name;
        const rowKey = field.meta?.row_key || "";
        const matchingOrderField = findMatchingOrderField(
          backendFieldKey,
          displayLabel,
          rowKey,
        );
        const resolvedSectionName =
          field.meta?.section_name || matchingOrderField?.sectionName || "";

        // Handle Office field - dynamically update dropdown options based on selected Country
        if (
          doesFieldMatchLocationCandidates(
            field,
            LOCATION_LOOKUP_FIELD_CANDIDATES.office,
          )
        ) {
          // Find the country field to get its selected value
          const countryField = baseUiFields.find((candidateField) =>
            doesFieldMatchLocationCandidates(
              candidateField,
              LOCATION_LOOKUP_FIELD_CANDIDATES.country,
            ),
          );

          const selectedCountryValue = countryField
            ? (candidateFormValues[countryField.field_name] ??
              countryField.updatedValue ??
              "")
            : "";

          // Get office options filtered by the selected country
          const dynamicOfficeOptions =
            getOfficeOptionsForCountry(selectedCountryValue);
          const fallbackOfficeOptions =
            field.meta?.dropdown_options?.length > 0
              ? field.meta.dropdown_options
              : getDropdownOptions(matchingOrderField);
          const resolvedOfficeOptions =
            dynamicOfficeOptions.length > 0
              ? dynamicOfficeOptions
              : fallbackOfficeOptions;

          return {
            ...field,
            meta: {
              ...field.meta,
              dropdown:
                resolvedOfficeOptions.length > 0
                  ? true
                  : Boolean(field.meta?.dropdown),
              dropdown_options: resolvedOfficeOptions,
              value_format:
                resolvedOfficeOptions.length > 0
                  ? "dropdown"
                  : field.meta?.value_format,
              data_type:
                resolvedOfficeOptions.length > 0
                  ? "dropdown"
                  : field.meta?.data_type,
            },
          };
        }

        if (
          resolvedSectionName !== SAVING_SECTION_NAME ||
          !doesFieldMatchCandidates(
            {
              field: backendFieldKey,
              labelToShowOnUI: displayLabel,
            },
            ["ReasonId", "Reason"],
          )
        ) {
          return field;
        }

        const applicationUiField = baseUiFields.find((candidateField) => {
          const candidateBackendField =
            candidateField.meta?.backend_field || candidateField.field_name;
          const candidateDisplayLabel =
            candidateField.meta?.display_label || candidateField.field_name;
          const candidateRowKey = candidateField.meta?.row_key || "";
          const candidateSectionName =
            candidateField.meta?.section_name ||
            findMatchingOrderField(
              candidateBackendField,
              candidateDisplayLabel,
              candidateRowKey,
            )?.sectionName ||
            "";

          if (candidateSectionName !== SAVING_SECTION_NAME) {
            return false;
          }

          if (rowKey && candidateRowKey !== rowKey) {
            return false;
          }

          return doesFieldMatchCandidates(
            {
              field: candidateBackendField,
              labelToShowOnUI: candidateDisplayLabel,
            },
            ["ApplicationUsedId", "Application Used"],
          );
        });

        const applicationOrderField = applicationUiField
          ? findMatchingOrderField(
              applicationUiField.meta?.backend_field ||
                applicationUiField.field_name,
              applicationUiField.meta?.display_label ||
                applicationUiField.field_name,
              applicationUiField.meta?.row_key || "",
            )
          : null;
        const selectedApplicationValue = applicationUiField
          ? (candidateFormValues[applicationUiField.field_name] ??
            applicationUiField.updatedValue ??
            "")
          : "";
        const matchedApplicationLookup =
          findMatchedApplicationUsedHoursReasonEntry(
            applicationOrderField,
            selectedApplicationValue,
          );
        const dynamicReasonOptions = buildSavingReasonOptionsFromLookup(
          matchedApplicationLookup,
        );
        const fallbackReasonOptions =
          field.meta?.dropdown_options?.length > 0
            ? field.meta.dropdown_options
            : getDropdownOptions(matchingOrderField);
        const resolvedReasonOptions =
          dynamicReasonOptions.length > 0
            ? dynamicReasonOptions
            : fallbackReasonOptions;

        return {
          ...field,
          meta: {
            ...field.meta,
            dropdown:
              resolvedReasonOptions.length > 0
                ? true
                : Boolean(field.meta?.dropdown),
            dropdown_options: resolvedReasonOptions,
            value_format:
              resolvedReasonOptions.length > 0
                ? "dropdown"
                : field.meta?.value_format,
            data_type:
              resolvedReasonOptions.length > 0
                ? "dropdown"
                : field.meta?.data_type,
          },
        };
      });
    },
    [
      baseUiFields,
      doesFieldMatchLocationCandidates,
      findMatchedApplicationUsedHoursReasonEntry,
      findMatchingOrderField,
      getOfficeOptionsForCountry,
    ],
  );

  const resolvedUiFields = useMemo(
    () => resolveUiFields(formValues),
    [formValues, resolveUiFields],
  );

  const getUiFieldSectionName = useCallback(
    (field) => {
      const backendFieldKey = field.meta?.backend_field || field.field_name;
      const displayLabel = field.meta?.display_label || field.field_name;
      const rowKey = field.meta?.row_key || "";

      return (
        field.meta?.section_name ||
        findMatchingOrderField(backendFieldKey, displayLabel, rowKey)
          ?.sectionName ||
        ""
      );
    },
    [findMatchingOrderField],
  );

  const shouldShowUiField = useCallback(
    (field, candidateUiFields = [], candidateFormValues = {}) => {
      const backendFieldKey = field.meta?.backend_field || field.field_name;
      const displayLabel = field.meta?.display_label || field.field_name;
      const resolvedSectionName = getUiFieldSectionName(field);

      if (
        resolvedSectionName === SAVING_SECTION_NAME &&
        doesFieldMatchCandidates(
          {
            field: backendFieldKey,
            labelToShowOnUI: displayLabel,
          },
          ["OtherReason", "Other Reason"],
        )
      ) {
        const rowKey = field.meta?.row_key || "";
        const reasonField = candidateUiFields.find((candidateField) => {
          const candidateBackendField =
            candidateField.meta?.backend_field || candidateField.field_name;
          const candidateDisplayLabel =
            candidateField.meta?.display_label || candidateField.field_name;

          if ((candidateField.meta?.row_key || "") !== rowKey) {
            return false;
          }

          return (
            getUiFieldSectionName(candidateField) === SAVING_SECTION_NAME &&
            doesFieldMatchCandidates(
              {
                field: candidateBackendField,
                labelToShowOnUI: candidateDisplayLabel,
              },
              ["ReasonId", "Reason"],
            )
          );
        });

        if (!reasonField) {
          return true;
        }

        const selectedReasonValue =
          candidateFormValues[reasonField.field_name] ??
          reasonField.updatedValue ??
          "";

        return isOtherLikeDropdownSelection(reasonField, selectedReasonValue);
      }

      if (isOtherOnHoldReasonUiField(field)) {
        const rowKey = field.meta?.row_key || "";
        const onHoldReasonField = candidateUiFields.find((candidateField) => {
          if ((candidateField.meta?.row_key || "") !== rowKey) {
            return false;
          }

          const candidateSectionName = getUiFieldSectionName(candidateField);

          if (
            resolvedSectionName &&
            candidateSectionName &&
            candidateSectionName !== resolvedSectionName
          ) {
            return false;
          }

          return isOnHoldReasonUiField(candidateField);
        });

        if (!onHoldReasonField) {
          return true;
        }

        const selectedOnHoldReasonValue =
          candidateFormValues[onHoldReasonField.field_name] ??
          onHoldReasonField.updatedValue ??
          "";

        return isOtherLikeDropdownSelection(
          onHoldReasonField,
          selectedOnHoldReasonValue,
        );
      }

      if (isOtherCancellationReasonUiField(field)) {
        const rowKey = field.meta?.row_key || "";
        const cancellationReasonField = candidateUiFields.find(
          (candidateField) => {
            if ((candidateField.meta?.row_key || "") !== rowKey) {
              return false;
            }

            const candidateSectionName = getUiFieldSectionName(candidateField);

            if (
              resolvedSectionName &&
              candidateSectionName &&
              candidateSectionName !== resolvedSectionName
            ) {
              return false;
            }

            return isCancellationReasonUiField(candidateField);
          },
        );

        if (!cancellationReasonField) {
          return true;
        }

        const selectedCancellationReasonValue =
          candidateFormValues[cancellationReasonField.field_name] ??
          cancellationReasonField.updatedValue ??
          "";

        return isOtherLikeDropdownSelection(
          cancellationReasonField,
          selectedCancellationReasonValue,
        );
      }

      if (isOtherCleansingToolUiField(field)) {
        const rowKey = field.meta?.row_key || "";
        const cleansingToolField = candidateUiFields.find((candidateField) => {
          if ((candidateField.meta?.row_key || "") !== rowKey) {
            return false;
          }

          const candidateSectionName = getUiFieldSectionName(candidateField);

          if (
            resolvedSectionName &&
            candidateSectionName &&
            candidateSectionName !== resolvedSectionName
          ) {
            return false;
          }

          return isCleansingToolUiField(candidateField);
        });

        if (!cleansingToolField) {
          return true;
        }

        const selectedCleansingToolValue =
          candidateFormValues[cleansingToolField.field_name] ??
          cleansingToolField.updatedValue ??
          "";

        return isOtherLikeDropdownSelection(
          cleansingToolField,
          selectedCleansingToolValue,
        );
      }

      if (isOtherAnalyzerTypeUiField(field)) {
        const rowKey = field.meta?.row_key || "";
        const analyzerTypeField = candidateUiFields.find((candidateField) => {
          if ((candidateField.meta?.row_key || "") !== rowKey) {
            return false;
          }

          const candidateSectionName = getUiFieldSectionName(candidateField);

          if (
            resolvedSectionName &&
            candidateSectionName &&
            candidateSectionName !== resolvedSectionName
          ) {
            return false;
          }

          return isAnalyzerTypeUiField(candidateField);
        });

        if (!analyzerTypeField) {
          return true;
        }

        const selectedAnalyzerTypeValue =
          candidateFormValues[analyzerTypeField.field_name] ??
          analyzerTypeField.updatedValue ??
          "";

        return isOtherLikeDropdownSelection(
          analyzerTypeField,
          selectedAnalyzerTypeValue,
        );
      }

      if (isOtherMarketSegmentUiField(field)) {
        const rowKey = field.meta?.row_key || "";
        const marketSegmentField = candidateUiFields.find((candidateField) => {
          if ((candidateField.meta?.row_key || "") !== rowKey) {
            return false;
          }

          const candidateSectionName = getUiFieldSectionName(candidateField);

          if (
            resolvedSectionName &&
            candidateSectionName &&
            candidateSectionName !== resolvedSectionName
          ) {
            return false;
          }

          return isMarketSegmentUiField(candidateField);
        });

        if (!marketSegmentField) {
          return true;
        }

        const selectedMarketSegmentValue =
          candidateFormValues[marketSegmentField.field_name] ??
          marketSegmentField.updatedValue ??
          "";

        return isOtherLikeDropdownSelection(
          marketSegmentField,
          selectedMarketSegmentValue,
        );
      }

      if (isOtherErpUiField(field)) {
        const rowKey = field.meta?.row_key || "";
        const erpField = candidateUiFields.find((candidateField) => {
          if ((candidateField.meta?.row_key || "") !== rowKey) {
            return false;
          }

          const candidateSectionName = getUiFieldSectionName(candidateField);

          if (
            resolvedSectionName &&
            candidateSectionName &&
            candidateSectionName !== resolvedSectionName
          ) {
            return false;
          }

          return isErpNameUiField(candidateField);
        });

        if (!erpField) {
          return true;
        }
        const selectedErpValue =
          candidateFormValues[erpField.field_name] ??
          erpField.updatedValue ??
          "";

        return isOtherLikeDropdownSelection(erpField, selectedErpValue);
      }

      if (isOtherExtractionToolUiField(field)) {
        const rowKey = field.meta?.row_key || "";
        const extractionToolField = candidateUiFields.find((candidateField) => {
          if ((candidateField.meta?.row_key || "") !== rowKey) {
            return false;
          }

          const candidateSectionName = getUiFieldSectionName(candidateField);

          if (
            resolvedSectionName &&
            candidateSectionName &&
            candidateSectionName !== resolvedSectionName
          ) {
            return false;
          }

          return isExtractionToolUiField(candidateField);
        });

        if (!extractionToolField) {
          return true;
        }

        const selectedExtractionToolValue =
          candidateFormValues[extractionToolField.field_name] ??
          extractionToolField.updatedValue ??
          "";

        return isOtherLikeDropdownSelection(
          extractionToolField,
          selectedExtractionToolValue,
        );
      }

      if (isToolIssueDescriptionUiField(field)) {
        const toolIssueRowKey = field.meta?.row_key || "";
        const toolIssueTypeField = candidateUiFields.find((candidateField) => {
          if ((candidateField.meta?.row_key || "") !== toolIssueRowKey) {
            return false;
          }

          const candidateSectionName = getUiFieldSectionName(candidateField);

          if (
            resolvedSectionName &&
            candidateSectionName &&
            candidateSectionName !== resolvedSectionName
          ) {
            return false;
          }

          return isToolIssueTypeUiField(candidateField);
        });

        if (!toolIssueTypeField) {
          return true;
        }

        const selectedToolIssueTypeValue =
          candidateFormValues[toolIssueTypeField.field_name] ??
          toolIssueTypeField.updatedValue ??
          "";

        return isToolIssueDescriptionSelection(
          toolIssueTypeField,
          selectedToolIssueTypeValue,
        );
      }

      if (!isReprocessDescriptionUiField(field)) {
        return true;
      }

      const reprocessRowKey = field.meta?.row_key || "";
      const reprocessReasonField = candidateUiFields.find((candidateField) => {
        if ((candidateField.meta?.row_key || "") !== reprocessRowKey) {
          return false;
        }

        const candidateSectionName = getUiFieldSectionName(candidateField);

        if (
          resolvedSectionName &&
          candidateSectionName &&
          candidateSectionName !== resolvedSectionName
        ) {
          return false;
        }

        return isReprocessReasonUiField(candidateField);
      });

      if (!reprocessReasonField) {
        return true;
      }

      const selectedReprocessReasonValue =
        candidateFormValues[reprocessReasonField.field_name] ??
        reprocessReasonField.updatedValue ??
        "";

      return isReprocessDescriptionSelection(
        reprocessReasonField,
        selectedReprocessReasonValue,
      );
    },
    [getUiFieldSectionName],
  );

  const visibleResolvedUiFields = useMemo(
    () =>
      resolvedUiFields.filter((field) =>
        shouldShowUiField(field, resolvedUiFields, formValues),
      ),
    [formValues, resolvedUiFields, shouldShowUiField],
  );

  const findMatchingUiField = useCallback(
    (backendField, displayLabel = "", rowKey = "") =>
      resolvedUiFields.find((field) => {
        if (rowKey && field?.meta?.row_key && field.meta.row_key !== rowKey) {
          return false;
        }

        const normalizedBackendField = normalizeFieldIdentifier(backendField);
        const normalizedDisplayLabel = normalizeFieldIdentifier(displayLabel);

        return (
          normalizeFieldIdentifier(
            field?.meta?.backend_field || field?.field_name,
          ) === normalizedBackendField ||
          normalizeFieldIdentifier(
            field?.meta?.display_label || field?.field_name,
          ) === normalizedDisplayLabel
        );
      }) || null,
    [resolvedUiFields],
  );

  const shouldRenderUiFields =
    Boolean(
      message.ui_fields_flag || message.efforts_flag || message.savings_flag,
    ) && visibleResolvedUiFields.length > 0;

  const isOrderFieldEditable = useCallback(
    (orderField) => {
      if (!orderField) {
        return true;
      }

      const fieldName = String(
        orderField?.field || orderField?.baseFieldName || "",
      ).trim();

      if (isManuallyEditableAutoPopulatedSectionField(orderField)) {
        return true;
      }

      if (isRegionalLead) {
        if (typeof orderField?.isEditableByRL === "boolean") {
          return orderField.isEditableByRL;
        }

        return true;
      }

      if (isDidRole) {
        if (typeof orderField.isEditable === "boolean") {
          return orderField.isEditable;
        }

        if (typeof orderField.editable === "boolean") {
          return orderField.editable;
        }
      }

      return true;
    },
    [isDidRole, isRegionalLead],
  );

  const isUiFieldEditable = useCallback(
    (field) => {
      const backendFieldKey = field?.meta?.backend_field || field?.field_name;
      const displayLabel = field?.meta?.display_label || field?.field_name;
      const rowKey = field?.meta?.row_key || "";

      return isOrderFieldEditable(
        findMatchingOrderField(backendFieldKey, displayLabel, rowKey),
      );
    },
    [findMatchingOrderField, isOrderFieldEditable],
  );

  const isFieldEditableByKey = useCallback(
    (fieldKey, displayLabel = "", rowKey = "") =>
      isOrderFieldEditable(
        findMatchingOrderField(fieldKey, displayLabel, rowKey),
      ),
    [findMatchingOrderField, isOrderFieldEditable],
  );

  const getCurrentFieldValue = useCallback(
    (
      backendField,
      displayLabel = "",
      candidateFormValues = formValues,
      rowKey = "",
    ) => {
      const matchingUiField = findMatchingUiField(
        backendField,
        displayLabel,
        rowKey,
      );

      if (matchingUiField) {
        if (
          Object.prototype.hasOwnProperty.call(
            candidateFormValues,
            matchingUiField.field_name,
          )
        ) {
          return candidateFormValues[matchingUiField.field_name] ?? "";
        }

        return matchingUiField.updatedValue ?? "";
      }

      const matchingOrderField = findMatchingOrderField(
        backendField,
        displayLabel,
        rowKey,
      );

      if (matchingOrderField) {
        return (
          matchingOrderField.fieldValue ??
          getRowDataValueByCandidates(
            matchingOrderField.rowDataInfo,
            [backendField, displayLabel],
            "",
          )
        );
      }

      return "";
    },
    [findMatchingOrderField, findMatchingUiField, formValues],
  );

  const getEffortContributorFieldKey = useCallback(
    (selectedRole, roleField = null) => {
      const normalizedSelectedRole = String(selectedRole || "").trim();

      if (!normalizedSelectedRole) {
        return "";
      }

      const resolvedRoleField =
        roleField || findMatchingOrderField("Role", "Role");
      const matchingRoleOption = getDropdownOptions(resolvedRoleField).find(
        (option) =>
          doesDropdownOptionMatchValue(option, normalizedSelectedRole),
      );
      const resolvedRoleLabel = matchingRoleOption
        ? getDropdownOptionLabel(matchingRoleOption)
        : normalizedSelectedRole;

      return (
        CONTRIBUTOR_ROLE_FIELD_MAP[
          String(resolvedRoleLabel || "")
            .trim()
            .toLowerCase()
        ] || ""
      );
    },
    [findMatchingOrderField],
  );

  const getFieldSectionName = useCallback(
    (backendField, displayLabel = "") => {
      const normalizedBackendField = normalizeFieldIdentifier(backendField);

      if (EFFORT_FIELD_IDENTIFIERS.has(normalizedBackendField)) {
        return EFFORT_SECTION_NAME;
      }

      if (SAVING_FIELD_IDENTIFIERS.has(normalizedBackendField)) {
        return SAVING_SECTION_NAME;
      }

      return (
        findMatchingOrderField(backendField, displayLabel)?.sectionName || ""
      );
    },
    [findMatchingOrderField],
  );

  const getSectionRowField = useCallback(
    (row, candidates = []) =>
      (row?.fields || []).find((field) =>
        doesFieldMatchCandidates(field, candidates),
      ) || null,
    [],
  );

  const getSectionRowFieldValue = useCallback(
    (row, candidates = [], fallbackValue = "") => {
      const rowField = getSectionRowField(row, candidates);

      if (rowField) {
        const matchingUiField = findMatchingUiField(
          rowField.field,
          rowField.labelToShowOnUI,
          row.rowKey,
        );

        if (
          matchingUiField &&
          Object.prototype.hasOwnProperty.call(
            formValues,
            matchingUiField.field_name,
          )
        ) {
          const currentValue = formValues[matchingUiField.field_name] ?? "";

          if (
            String(currentValue ?? "").trim() !== "" &&
            String(currentValue ?? "") !==
              String(matchingUiField.updatedValue ?? "")
          ) {
            return getDropdownDisplayMatchValue(rowField, currentValue);
          }

          return currentValue;
        }

        if (rowField.fieldValue !== undefined && rowField.fieldValue !== null) {
          return rowField.fieldValue;
        }
      }

      return getRowDataValueByCandidates(
        row?.rowDataInfo,
        candidates,
        fallbackValue,
      );
    },
    [findMatchingUiField, formValues, getSectionRowField],
  );

  const getSubmittedUiFieldValue = useCallback(
    (field) => {
      const rawValue = formValues[field.field_name] || "";

      if (String(rawValue ?? "").trim() === "") {
        return "";
      }

      const backendFieldKey = field.meta?.backend_field || field.field_name;
      const displayLabel = field.meta?.display_label || field.field_name;
      const rowKey = field.meta?.row_key || "";
      const matchingOrderField = findMatchingOrderField(
        backendFieldKey,
        displayLabel,
        rowKey,
      );
      const dropdownOptions =
        field.meta?.dropdown_options?.length > 0
          ? field.meta.dropdown_options
          : getDropdownOptions(matchingOrderField);
      const normalizedValueFormat = String(
        field.meta?.value_format ||
          field.meta?.data_type ||
          matchingOrderField?.dataType ||
          "",
      )
        .trim()
        .toLowerCase();
      const isDropdown =
        Boolean(field.meta?.dropdown) ||
        ["dropdown", "select"].includes(normalizedValueFormat) ||
        dropdownOptions.length > 0;

      if (!isDropdown) {
        return rawValue;
      }

      const dropdownField =
        dropdownOptions.length > 0
          ? { fieldValueList: dropdownOptions }
          : matchingOrderField;

      return getDropdownDisplayMatchValue(dropdownField, rawValue);
    },
    [findMatchingOrderField, formValues],
  );

  const isSubmittedUiFieldChanged = useCallback(
    (field) => {
      const backendFieldKey = field.meta?.backend_field || field.field_name;
      const displayLabel = field.meta?.display_label || field.field_name;
      const rowKey = field.meta?.row_key || "";
      const matchingOrderField = findMatchingOrderField(
        backendFieldKey,
        displayLabel,
        rowKey,
      );
      const submittedValue = getSubmittedUiFieldValue(field);
      const originalValue = matchingOrderField
        ? (matchingOrderField.fieldValue ??
          getRowDataValueByCandidates(
            matchingOrderField.rowDataInfo,
            [backendFieldKey, displayLabel],
            "",
          ))
        : "";
      const dropdownOptions =
        field.meta?.dropdown_options?.length > 0
          ? field.meta.dropdown_options
          : getDropdownOptions(matchingOrderField);
      const normalizedValueFormat = String(
        field.meta?.value_format ||
          field.meta?.data_type ||
          matchingOrderField?.dataType ||
          "",
      )
        .trim()
        .toLowerCase();
      const isDropdown =
        Boolean(field.meta?.dropdown) ||
        ["dropdown", "select"].includes(normalizedValueFormat) ||
        dropdownOptions.length > 0;

      if (isDropdown) {
        const dropdownField =
          dropdownOptions.length > 0
            ? { fieldValueList: dropdownOptions }
            : matchingOrderField;

        return (
          String(
            getDropdownDisplayMatchValue(dropdownField, submittedValue) || "",
          ).trim() !==
          String(
            getDropdownDisplayMatchValue(dropdownField, originalValue) || "",
          ).trim()
        );
      }

      return (
        String(submittedValue ?? "").trim() !==
        String(originalValue ?? "").trim()
      );
    },
    [findMatchingOrderField, getSubmittedUiFieldValue],
  );

  const buildEffortPayloadRecords = useCallback(
    () =>
      effortSectionRows.map((row) => ({
        EffortForId: getStringIdentifierValue(
          getSectionRowFieldValue(row, ["EffortForId"], ""),
        ),
        effortHours: getSectionRowFieldValue(
          row,
          ["EffortHours", "Effort Hours", "Effort In Hour", "Efforts In Hours"],
          "",
        ),
        role: getSectionRowFieldValue(row, ["Role"], ""),
        resourceName: getSectionRowFieldValue(
          row,
          ["ResourceName", "Resource Name"],
          "",
        ),
        reason: getSectionRowFieldValue(row, ["Reason"], ""),
        effortForMasterId: getStringIdentifierValue(
          getSectionRowFieldValue(
            row,
            ["EffortForMasterId", "EffortFor", "Effort For", "Efforts For"],
            "",
          ),
        ),
      })),
    [effortSectionRows, getSectionRowFieldValue],
  );

  const buildSavingPayloadRecords = useCallback(
    () =>
      savingSectionRows.map((row) => {
        const resolvedUserName = getSectionRowFieldValue(
          row,
          ["UserName", "Username", "User Name"],
          "",
        );

        return {
          ID: getStringIdentifierValue(
            getSectionRowFieldValue(row, ["ID", "SavingId"], ""),
          ),
          hoursToSave: getSectionRowFieldValue(
            row,
            ["HoursToSave", "Hours To Save", "Hours Saved"],
            "",
          ),
          applicationUsedId: getStringIdentifierValue(
            getSectionRowFieldValue(
              row,
              ["ApplicationUsedId", "Application Used"],
              "",
            ),
          ),
          otherReason: getSectionRowFieldValue(
            row,
            ["OtherReason", "Other Reason"],
            "",
          ),
          reasonId: getStringIdentifierValue(
            getSectionRowFieldValue(row, ["ReasonId", "Reason"], ""),
          ),
          userName: resolvedUserName || userEmail,
        };
      }),
    [getSectionRowFieldValue, savingSectionRows, userEmail],
  );

  const nowMinDateTime = useMemo(() => {
    return new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 16);
  }, []);

  // Validate field value using fieldValidations.config rules
  const validateFieldValue = useCallback(
    (
      fieldKey,
      value,
      valueFormat,
      allFieldValues = [],
      displayLabel = "",
      fieldContext = null,
    ) => {
      const errors = [];
      const validation = getFieldValidation(
        resolveValidationFieldKey(fieldKey, displayLabel),
      );
      const rowKey = fieldContext?.meta?.row_key || "";
      const matchingOrderField = findMatchingOrderField(
        fieldKey,
        displayLabel,
        rowKey,
      );
      const dropdownOptions =
        fieldContext?.meta?.dropdown_options?.length > 0
          ? fieldContext.meta.dropdown_options
          : getDropdownOptions(matchingOrderField);
      const requestedValueFormat = String(
        fieldContext?.meta?.data_type ||
          fieldContext?.meta?.value_format ||
          valueFormat ||
          matchingOrderField?.dataType ||
          "",
      )
        .trim()
        .toLowerCase();
      const isDropdown =
        Boolean(fieldContext?.meta?.dropdown) ||
        ["dropdown", "select"].includes(requestedValueFormat) ||
        dropdownOptions.length > 0;
      const normalizedValueFormat = String(
        isDropdown ? "dropdown" : requestedValueFormat,
      ).toLowerCase();
      const normalizedValue =
        typeof value === "string" ? value : String(value ?? "");
      const hasValidCalendarDateValue =
        normalizedValueFormat === "date" && isValidDateValue(value);

      const isRequired = Boolean(
        fieldContext?.meta?.required ||
        validation?.required ||
        matchingOrderField?.isRequired ||
        matchingOrderField?.required,
      );
      const resolvedSectionName =
        fieldContext?.meta?.section_name ||
        matchingOrderField?.sectionName ||
        "";
      const isSavingsApplicationUsedField =
        resolvedSectionName === SAVING_SECTION_NAME &&
        doesFieldMatchCandidates(
          {
            field: fieldKey,
            labelToShowOnUI: displayLabel,
          },
          ["ApplicationUsedId", "Application Used"],
        );
      const isEffortForField =
        resolvedSectionName === EFFORT_SECTION_NAME &&
        doesFieldMatchCandidates(
          {
            field: fieldKey,
            labelToShowOnUI: displayLabel,
          },
          ["EffortForMasterId", "EffortFor", "Effort For", "Efforts For"],
        );
      const validationField =
        dropdownOptions.length > 0
          ? { fieldValueList: dropdownOptions }
          : matchingOrderField;

      const hasMatchingDropdownValue =
        isSavingsApplicationUsedField || isEffortForField
          ? Boolean(
              findDropdownOptionByMatchValue(validationField, normalizedValue),
            )
          : doesDropdownValueMatchOptions(validationField, normalizedValue);
      const normalizedRegexValue =
        validation?.regexTarget === "displayValue"
          ? getDropdownDisplayMatchValue(validationField, normalizedValue)
          : normalizedValue;
      if (!isOrderFieldEditable(matchingOrderField)) {
        return NON_EDITABLE_FIELD_MESSAGE;
      }

      if (isRequired && (!value || String(value).trim() === "")) {
        errors.push(
          validation?.requiredMessage ||
            fieldContext?.meta?.requiredMessage ||
            matchingOrderField?.requiredMessage ||
            "This field is required",
        );
        return errors.join(". ");
      }

      if (
        isRequired &&
        ["dropdown", "select"].includes(normalizedValueFormat) &&
        normalizedValue &&
        !hasMatchingDropdownValue
      ) {
        errors.push(
          validation?.requiredMessage ||
            fieldContext?.meta?.requiredMessage ||
            matchingOrderField?.requiredMessage ||
            "This field is required",
        );
        return errors.join(". ");
      }

      if (!value || String(value).trim() === "") {
        return "";
      }

      if (normalizedValueFormat === "date" && !hasValidCalendarDateValue) {
        errors.push(INVALID_DATE_MESSAGE);
        return errors.join(". ");
      }

      if (
        isDateRelationshipField(fieldKey) &&
        value &&
        normalizedValueFormat === "date"
      ) {
        const dateRelationshipValidation = validateDateRelationships(
          fieldKey,
          value,
          allFieldValues,
          orderDetails,
        );
        if (!dateRelationshipValidation.isValid) {
          errors.push(dateRelationshipValidation.message);
        }
      }

      if (validation?.regex && !validation.regex.test(normalizedRegexValue)) {
        errors.push(validation.message || "Invalid format");
      }

      if (validation?.min !== undefined || validation?.max !== undefined) {
        const numValue = parseFloat(normalizedValue);
        if (!isNaN(numValue)) {
          if (validation.min !== undefined && numValue < validation.min) {
            errors.push(
              validation.message || `Value must be at least ${validation.min}`,
            );
          }
          if (validation.max !== undefined && numValue > validation.max) {
            errors.push(
              validation.message || `Value must be at most ${validation.max}`,
            );
          }
        }
      }

      if (
        validation?.minLength !== undefined &&
        normalizedValue.length < validation.minLength
      ) {
        errors.push(
          validation.message ||
            `Minimum ${validation.minLength} characters required`,
        );
      }
      if (
        validation?.maxLength !== undefined &&
        normalizedValue.length > validation.maxLength
      ) {
        errors.push(
          validation.message ||
            `Maximum ${validation.maxLength} characters allowed`,
        );
      }

      if (
        !validation?.regex &&
        ["number", "numerical"].includes(normalizedValueFormat) &&
        !/^\d*\.?\d*$/.test(normalizedValue)
      ) {
        errors.push("Only numbers are allowed");
      }

      if (
        isRequired &&
        ["dropdown", "select"].includes(normalizedValueFormat) &&
        !hasMatchingDropdownValue
      ) {
        errors.push(INVALID_DROPDOWN_OPTION_MESSAGE);
      }

      if (isContributorField(fieldKey) && value) {
        const contributorValidation = validateContributorUniqueness(
          fieldKey,
          value,
          allFieldValues,
        );
        if (!contributorValidation.isValid) {
          errors.push(contributorValidation.message);
        }
      }

      return errors.join(". ");
    },
    [findMatchingOrderField, isOrderFieldEditable, orderDetails],
  );

  const buildValidationErrors = useCallback(
    (candidateFormValues) => {
      const candidateUiFields = resolveUiFields(candidateFormValues);
      const candidateVisibleUiFields = candidateUiFields.filter((field) =>
        shouldShowUiField(field, candidateUiFields, candidateFormValues),
      );
      if (
        !Array.isArray(candidateVisibleUiFields) ||
        candidateVisibleUiFields.length === 0
      ) {
        return {};
      }

      const nextValidationErrors = {};
      const allFieldValues = candidateVisibleUiFields.map((field) => ({
        fieldName: field.meta?.backend_field || field.field_name,
        updatedValue: candidateFormValues[field.field_name] || "",
      }));

      candidateVisibleUiFields.forEach((field) => {
        const fieldKey = field.meta?.backend_field || field.field_name;
        const displayLabel = field.meta?.display_label || field.field_name;
        const valueFormat = field.meta?.value_format || "text";
        const validationMessage = validateFieldValue(
          fieldKey,
          candidateFormValues[field.field_name] || "",
          valueFormat,
          allFieldValues,
          displayLabel,
          field,
        );

        if (validationMessage) {
          nextValidationErrors[field.field_name] = validationMessage;
        }
      });

      return nextValidationErrors;
    },
    [resolveUiFields, shouldShowUiField, validateFieldValue],
  );

  const syncEffortResourceNameForRole = useCallback(
    (candidateFormValues, selectedRole, rowKey = "", roleField = null) => {
      const resourceNameField = findMatchingUiField(
        "ResourceName",
        "Resource Name",
        rowKey,
      );

      if (!resourceNameField) {
        return candidateFormValues;
      }

      const contributorFieldKey = getEffortContributorFieldKey(
        selectedRole,
        roleField,
      );

      if (!contributorFieldKey) {
        return candidateFormValues;
      }

      const contributorValue = getCurrentFieldValue(
        contributorFieldKey,
        contributorFieldKey,
        candidateFormValues,
      );

      if (String(contributorValue || "").trim() === "") {
        if (
          String(candidateFormValues[resourceNameField.field_name] || "") === ""
        ) {
          return candidateFormValues;
        }

        return {
          ...candidateFormValues,
          [resourceNameField.field_name]: "",
        };
      }

      if (
        String(candidateFormValues[resourceNameField.field_name] || "") ===
        String(contributorValue)
      ) {
        return candidateFormValues;
      }

      return {
        ...candidateFormValues,
        [resourceNameField.field_name]: contributorValue,
      };
    },
    [findMatchingUiField, getCurrentFieldValue, getEffortContributorFieldKey],
  );

  const syncEffortResourceNamesForContributorField = useCallback(
    (candidateFormValues, contributorFieldKey) => {
      if (!isContributorField(contributorFieldKey)) {
        return candidateFormValues;
      }

      let nextFormValues = candidateFormValues;

      effortSectionRows.forEach((row) => {
        const roleField = getSectionRowField(row, ["Role"]);

        if (!roleField) {
          return;
        }

        const selectedRole = getCurrentFieldValue(
          roleField.field,
          roleField.labelToShowOnUI,
          nextFormValues,
          row.rowKey,
        );
        const mappedContributorFieldKey = getEffortContributorFieldKey(
          selectedRole,
          roleField,
        );

        if (mappedContributorFieldKey !== contributorFieldKey) {
          return;
        }

        nextFormValues = syncEffortResourceNameForRole(
          nextFormValues,
          selectedRole,
          row.rowKey,
          roleField,
        );
      });

      return nextFormValues;
    },
    [
      effortSectionRows,
      getCurrentFieldValue,
      getEffortContributorFieldKey,
      getSectionRowField,
      syncEffortResourceNameForRole,
    ],
  );

  // Update form values when the AI fields or generated section fields change.
  useEffect(() => {
    if (baseUiFields.length === 0) {
      setFormValues({});
      setFieldValidationErrors({});
      return;
    }

    const newValues = buildInitialFormValues(baseUiFields);
    const validationErrors = buildValidationErrors(newValues);

    setFormValues(newValues);
    setFieldValidationErrors(validationErrors);
  }, [baseUiFields, buildValidationErrors]);

  const now = new Date();
  const day = now.toLocaleDateString(undefined, {
    weekday: "long",
  });
  const time = now.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });

  const fieldName = message.fieldName || "Activity ID";
  const currentValue = message.currentValue || "112840";
  const currentValueMembers = isAdditionalDidField(fieldName)
    ? normalizeListDisplayValue(currentValue)
    : [];

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  const handleFieldChange = (
    fieldName,
    value,
    valueFormat,
    backendFieldKey,
    displayLabel = "",
  ) => {
    const fieldKey = backendFieldKey || fieldName;
    const changedField = resolvedUiFields.find(
      (field) => field.field_name === fieldName,
    );
    const rowKey = changedField?.meta?.row_key || "";

    if (!isFieldEditableByKey(fieldKey, displayLabel, rowKey)) {
      setFieldValidationErrors((prev) => ({
        ...prev,
        [fieldName]: NON_EDITABLE_FIELD_MESSAGE,
      }));
      return;
    }

    // Update form values first
    let updatedFormValues = {
      ...formValues,
      [fieldName]: value,
    };

    if (
      getFieldSectionName(fieldKey, displayLabel) === EFFORT_SECTION_NAME &&
      normalizeFieldIdentifier(fieldKey) === "role"
    ) {
      const roleOrderField = findMatchingOrderField(
        fieldKey,
        displayLabel,
        rowKey,
      );
      updatedFormValues = syncEffortResourceNameForRole(
        updatedFormValues,
        value,
        rowKey,
        roleOrderField,
      );
    }

    if (isContributorField(fieldKey)) {
      updatedFormValues = syncEffortResourceNamesForContributorField(
        updatedFormValues,
        fieldKey,
      );
    }

    // When CountryId/Country changes, reset OfficeId/OfficeLocation to empty
    if (
      doesFieldMatchLocationCandidates(
        changedField || { field: fieldKey, labelToShowOnUI: displayLabel },
        LOCATION_LOOKUP_FIELD_CANDIDATES.country,
      )
    ) {
      const candidateUiFields = resolveUiFields(updatedFormValues);
      const officeField = candidateUiFields.find((field) =>
        doesFieldMatchLocationCandidates(
          field,
          LOCATION_LOOKUP_FIELD_CANDIDATES.office,
        ),
      );

      if (officeField) {
        updatedFormValues[officeField.field_name] = "";
      }
    }

    if (
      getFieldSectionName(fieldKey, displayLabel) === SAVING_SECTION_NAME &&
      normalizeFieldIdentifier(fieldKey) === "applicationusedid"
    ) {
      const candidateUiFields = resolveUiFields(updatedFormValues);
      const reasonField = candidateUiFields.find((field) => {
        const candidateBackendField =
          field.meta?.backend_field || field.field_name;

        if ((field.meta?.row_key || "") !== rowKey) {
          return false;
        }

        return doesFieldMatchCandidates({ field: candidateBackendField }, [
          "ReasonId",
          "Reason",
        ]);
      });
      const otherReasonField = candidateUiFields.find((field) => {
        const candidateBackendField =
          field.meta?.backend_field || field.field_name;

        if ((field.meta?.row_key || "") !== rowKey) {
          return false;
        }

        return doesFieldMatchCandidates({ field: candidateBackendField }, [
          "OtherReason",
          "Other Reason",
        ]);
      });
      const hoursField = candidateUiFields.find((field) => {
        const candidateBackendField =
          field.meta?.backend_field || field.field_name;

        if ((field.meta?.row_key || "") !== rowKey) {
          return false;
        }

        return doesFieldMatchCandidates({ field: candidateBackendField }, [
          "HoursToSave",
          "Hours To Save",
          "Hours Saved",
        ]);
      });
      const applicationOrderField = findMatchingOrderField(
        fieldKey,
        displayLabel,
        rowKey,
      );
      const matchedApplicationLookup =
        findMatchedApplicationUsedHoursReasonEntry(
          applicationOrderField,
          value,
        );
      const defaultHoursValue = formatSavingDefaultHoursValue(
        matchedApplicationLookup?.defaultHours,
      );

      if (reasonField) {
        updatedFormValues[reasonField.field_name] = "";
      }

      if (otherReasonField) {
        updatedFormValues[otherReasonField.field_name] = "";
      }

      if (hoursField && defaultHoursValue) {
        updatedFormValues[hoursField.field_name] = defaultHoursValue;
      }
    }

    if (
      getFieldSectionName(fieldKey, displayLabel) === SAVING_SECTION_NAME &&
      normalizeFieldIdentifier(fieldKey) === "reasonid"
    ) {
      const candidateUiFields = resolveUiFields(updatedFormValues);
      const reasonField =
        candidateUiFields.find((field) => field.field_name === fieldName) ||
        changedField;
      const otherReasonField = candidateUiFields.find((field) => {
        const candidateBackendField =
          field.meta?.backend_field || field.field_name;

        if ((field.meta?.row_key || "") !== rowKey) {
          return false;
        }

        return doesFieldMatchCandidates({ field: candidateBackendField }, [
          "OtherReason",
          "Other Reason",
        ]);
      });

      if (
        reasonField &&
        otherReasonField &&
        !isOtherLikeDropdownSelection(reasonField, value)
      ) {
        updatedFormValues[otherReasonField.field_name] = "";
      }
    }

    if (changedField && isOnHoldReasonUiField(changedField)) {
      const candidateUiFields = resolveUiFields(updatedFormValues);
      const changedSectionName = getUiFieldSectionName(changedField);
      const otherOnHoldReasonField = candidateUiFields.find(
        (candidateField) => {
          if ((candidateField.meta?.row_key || "") !== rowKey) {
            return false;
          }

          const candidateSectionName = getUiFieldSectionName(candidateField);

          if (
            changedSectionName &&
            candidateSectionName &&
            candidateSectionName !== changedSectionName
          ) {
            return false;
          }

          return isOtherOnHoldReasonUiField(candidateField);
        },
      );

      if (
        otherOnHoldReasonField &&
        !isOtherLikeDropdownSelection(changedField, value)
      ) {
        updatedFormValues[otherOnHoldReasonField.field_name] = "";
      }
    }

    if (changedField && isCancellationReasonUiField(changedField)) {
      const candidateUiFields = resolveUiFields(updatedFormValues);
      const changedSectionName = getUiFieldSectionName(changedField);
      const otherCancellationReasonField = candidateUiFields.find(
        (candidateField) => {
          if ((candidateField.meta?.row_key || "") !== rowKey) {
            return false;
          }

          const candidateSectionName = getUiFieldSectionName(candidateField);

          if (
            changedSectionName &&
            candidateSectionName &&
            candidateSectionName !== changedSectionName
          ) {
            return false;
          }

          return isOtherCancellationReasonUiField(candidateField);
        },
      );

      if (
        otherCancellationReasonField &&
        !isOtherLikeDropdownSelection(changedField, value)
      ) {
        updatedFormValues[otherCancellationReasonField.field_name] = "";
      }
    }

    if (changedField && isCleansingToolUiField(changedField)) {
      const candidateUiFields = resolveUiFields(updatedFormValues);
      const changedSectionName = getUiFieldSectionName(changedField);
      const otherCleansingToolField = candidateUiFields.find(
        (candidateField) => {
          if ((candidateField.meta?.row_key || "") !== rowKey) {
            return false;
          }

          const candidateSectionName = getUiFieldSectionName(candidateField);

          if (
            changedSectionName &&
            candidateSectionName &&
            candidateSectionName !== changedSectionName
          ) {
            return false;
          }

          return isOtherCleansingToolUiField(candidateField);
        },
      );

      if (
        otherCleansingToolField &&
        !isOtherLikeDropdownSelection(changedField, value)
      ) {
        updatedFormValues[otherCleansingToolField.field_name] = "";
      }
    }

    if (changedField && isAnalyzerTypeUiField(changedField)) {
      const candidateUiFields = resolveUiFields(updatedFormValues);
      const changedSectionName = getUiFieldSectionName(changedField);
      const otherAnalyzerTypeField = candidateUiFields.find(
        (candidateField) => {
          if ((candidateField.meta?.row_key || "") !== rowKey) {
            return false;
          }

          const candidateSectionName = getUiFieldSectionName(candidateField);

          if (
            changedSectionName &&
            candidateSectionName &&
            candidateSectionName !== changedSectionName
          ) {
            return false;
          }

          return isOtherAnalyzerTypeUiField(candidateField);
        },
      );

      if (
        otherAnalyzerTypeField &&
        !isOtherLikeDropdownSelection(changedField, value)
      ) {
        updatedFormValues[otherAnalyzerTypeField.field_name] = "";
      }
    }

    if (changedField && isMarketSegmentUiField(changedField)) {
      const candidateUiFields = resolveUiFields(updatedFormValues);
      const changedSectionName = getUiFieldSectionName(changedField);
      const otherMarketSegmentField = candidateUiFields.find(
        (candidateField) => {
          if ((candidateField.meta?.row_key || "") !== rowKey) {
            return false;
          }

          const candidateSectionName = getUiFieldSectionName(candidateField);

          if (
            changedSectionName &&
            candidateSectionName &&
            candidateSectionName !== changedSectionName
          ) {
            return false;
          }

          return isOtherMarketSegmentUiField(candidateField);
        },
      );

      if (
        otherMarketSegmentField &&
        !isOtherLikeDropdownSelection(changedField, value)
      ) {
        updatedFormValues[otherMarketSegmentField.field_name] = "";
      }
    }

    if (changedField && isErpNameUiField(changedField)) {
      const candidateUiFields = resolveUiFields(updatedFormValues);
      const changedSectionName = getUiFieldSectionName(changedField);
      const otherErpField = candidateUiFields.find((candidateField) => {
        if ((candidateField.meta?.row_key || "") !== rowKey) {
          return false;
        }

        const candidateSectionName = getUiFieldSectionName(candidateField);

        if (
          changedSectionName &&
          candidateSectionName &&
          candidateSectionName !== changedSectionName
        ) {
          return false;
        }

        return isOtherErpUiField(candidateField);
      });

      if (otherErpField && !isOtherLikeDropdownSelection(changedField, value)) {
        updatedFormValues[otherErpField.field_name] = "";
      }
    }

    if (changedField && isExtractionToolUiField(changedField)) {
      const candidateUiFields = resolveUiFields(updatedFormValues);
      const changedSectionName = getUiFieldSectionName(changedField);
      const otherExtractionToolField = candidateUiFields.find(
        (candidateField) => {
          if ((candidateField.meta?.row_key || "") !== rowKey) {
            return false;
          }

          const candidateSectionName = getUiFieldSectionName(candidateField);

          if (
            changedSectionName &&
            candidateSectionName &&
            candidateSectionName !== changedSectionName
          ) {
            return false;
          }

          return isOtherExtractionToolUiField(candidateField);
        },
      );

      if (
        otherExtractionToolField &&
        !isOtherLikeDropdownSelection(changedField, value)
      ) {
        updatedFormValues[otherExtractionToolField.field_name] = "";
      }
    }

    if (changedField && isToolIssueTypeUiField(changedField)) {
      const candidateUiFields = resolveUiFields(updatedFormValues);
      const changedSectionName = getUiFieldSectionName(changedField);
      const toolIssueDescriptionField = candidateUiFields.find(
        (candidateField) => {
          if ((candidateField.meta?.row_key || "") !== rowKey) {
            return false;
          }

          const candidateSectionName = getUiFieldSectionName(candidateField);

          if (
            changedSectionName &&
            candidateSectionName &&
            candidateSectionName !== changedSectionName
          ) {
            return false;
          }

          return isToolIssueDescriptionUiField(candidateField);
        },
      );

      if (
        toolIssueDescriptionField &&
        !isToolIssueDescriptionSelection(changedField, value)
      ) {
        updatedFormValues[toolIssueDescriptionField.field_name] = "";
      }
    }

    if (changedField && isReprocessReasonUiField(changedField)) {
      const candidateUiFields = resolveUiFields(updatedFormValues);
      const changedSectionName = getUiFieldSectionName(changedField);
      const reprocessDescriptionField = candidateUiFields.find(
        (candidateField) => {
          if ((candidateField.meta?.row_key || "") !== rowKey) {
            return false;
          }

          const candidateSectionName = getUiFieldSectionName(candidateField);

          if (
            changedSectionName &&
            candidateSectionName &&
            candidateSectionName !== changedSectionName
          ) {
            return false;
          }

          return isReprocessDescriptionUiField(candidateField);
        },
      );

      if (
        reprocessDescriptionField &&
        !isReprocessDescriptionSelection(changedField, value)
      ) {
        updatedFormValues[reprocessDescriptionField.field_name] = "";
      }
    }

    setFormValues(updatedFormValues);
    setFieldValidationErrors(buildValidationErrors(updatedFormValues));
  };

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitErrorDetails, setSubmitErrorDetails] = useState([]);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const hasFieldValidationErrors =
    Object.keys(fieldValidationErrors).length > 0;

  const handleSubmit = async () => {
    const submittedUiFields = resolvedUiFields.filter(
      (field) =>
        isUiFieldEditable(field) &&
        shouldShowUiField(field, resolvedUiFields, formValues),
    );

    // Human-in-the-loop checkpoint: send resume_input to the AI service instead
    // of invoking the update-order API.

    if (message.checkpoint_pending && submittedUiFields.length > 0) {
      const resumeInput = {};
      const resumeInputSummaryParts = [];

      submittedUiFields.forEach((field) => {
        const key = field.meta?.backend_field || field.field_name;
        const displayLabel = field.meta?.display_label || field.field_name;
        const submittedValue = getSubmittedUiFieldValue(field);

        resumeInput[key] = submittedValue;
        resumeInputSummaryParts.push(`${displayLabel}: ${submittedValue}`);
      });

      // When Country is being updated, ensure OfficeId is also included with empty value
      const isCountryBeingUpdated = Object.keys(resumeInput).some((key) =>
        LOCATION_LOOKUP_FIELD_CANDIDATES.country.some(
          (candidate) =>
            normalizeFieldIdentifier(key) ===
            normalizeFieldIdentifier(candidate),
        ),
      );

      if (isCountryBeingUpdated) {
        const hasOfficeField = Object.keys(resumeInput).some((key) =>
          LOCATION_LOOKUP_FIELD_CANDIDATES.office.some(
            (candidate) =>
              normalizeFieldIdentifier(key) ===
              normalizeFieldIdentifier(candidate),
          ),
        );

        if (!hasOfficeField) {
          const officeUiField = resolvedUiFields.find((field) =>
            doesFieldMatchLocationCandidates(
              field,
              LOCATION_LOOKUP_FIELD_CANDIDATES.office,
            ),
          );

          const officeFieldName = officeUiField
            ? officeUiField.meta?.backend_field || officeUiField.field_name
            : "OfficeId";

          resumeInput[officeFieldName] = "";
          resumeInputSummaryParts.push(`Office Location: `);
        }
      }

      const resumeInputSummaryText = resumeInputSummaryParts.join(", ");

      setIsSubmitting(true);
      setSubmitError(null);
      setSubmitErrorDetails([]);
      setSubmitSuccess(false);
      try {
        if (onActionClick) {
          await onActionClick({
            action: "resumeCheckpoint",
            resume_input: resumeInput,
            resume_input_summary_text: resumeInputSummaryText,
          });
        }
        setSubmitSuccess(true);
      } catch (error) {
        setSubmitError(
          error?.message || "Failed to confirm. Please try again.",
        );
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    if (submittedUiFields.length > 0) {
      setIsSubmitting(true);
      setSubmitError(null);
      setSubmitErrorDetails([]);
      setSubmitSuccess(false);

      try {
        const hasChangedEffortFields = submittedUiFields.some((field) => {
          const backendFieldKey = field.meta?.backend_field || field.field_name;
          const displayLabel = field.meta?.display_label || field.field_name;

          return (
            getFieldSectionName(backendFieldKey, displayLabel) ===
              EFFORT_SECTION_NAME && isSubmittedUiFieldChanged(field)
          );
        });

        const hasChangedSavingFields = submittedUiFields.some((field) => {
          const backendFieldKey = field.meta?.backend_field || field.field_name;
          const displayLabel = field.meta?.display_label || field.field_name;

          return (
            getFieldSectionName(backendFieldKey, displayLabel) ===
              SAVING_SECTION_NAME && isSubmittedUiFieldChanged(field)
          );
        });

        // Build the fields array for the payload

        const fields = submittedUiFields.map((field) => ({
          fieldName: field.meta?.backend_field || field.field_name,
          updatedValue: getSubmittedUiFieldValue(field),
        }));

        const partitionedFields = submittedUiFields.reduce(
          (accumulator, field) => {
            const backendFieldKey =
              field.meta?.backend_field || field.field_name;
            const displayLabel = field.meta?.display_label || field.field_name;
            const submittedValue = getSubmittedUiFieldValue(field);
            const sectionName = getFieldSectionName(
              backendFieldKey,
              displayLabel,
            );
            const mappedField = {
              fieldName: backendFieldKey,
              updatedValue: submittedValue,
            };

            if (sectionName === EFFORT_SECTION_NAME) {
              accumulator.effortFields.push(mappedField);
              return accumulator;
            }

            if (sectionName === SAVING_SECTION_NAME) {
              accumulator.savingFields.push(mappedField);
              return accumulator;
            }

            accumulator.fields.push(mappedField);
            return accumulator;
          },
          { fields: [], effortFields: [], savingFields: [] },
        );

        // When Country is being updated, ensure OfficeId is also included with empty value
        // This matches the behavior in OrdersDetailsDashboard left section
        const isCountryBeingUpdated = partitionedFields.fields.some((field) =>
          LOCATION_LOOKUP_FIELD_CANDIDATES.country.some(
            (candidate) =>
              normalizeFieldIdentifier(field.fieldName) ===
              normalizeFieldIdentifier(candidate),
          ),
        );

        if (isCountryBeingUpdated) {
          // Check if OfficeId/OfficeLocation is already in the fields array
          const hasOfficeField = partitionedFields.fields.some((field) =>
            LOCATION_LOOKUP_FIELD_CANDIDATES.office.some(
              (candidate) =>
                normalizeFieldIdentifier(field.fieldName) ===
                normalizeFieldIdentifier(candidate),
            ),
          );

          // If Office field is not already included, add it with empty value
          if (!hasOfficeField) {
            // Try to find the Office field name from the UI fields or order details
            const officeUiField = resolvedUiFields.find((field) =>
              doesFieldMatchLocationCandidates(
                field,
                LOCATION_LOOKUP_FIELD_CANDIDATES.office,
              ),
            );

            const officeFieldName = officeUiField
              ? officeUiField.meta?.backend_field || officeUiField.field_name
              : "OfficeId";

            partitionedFields.fields.push({
              fieldName: officeFieldName,
              updatedValue: "",
            });
          }
        }

        // Build payload matching OrdersDetailsDashboard format
        const payload = {
          requestId:
            orderDetails?.[0]?.headerSection?.[0]?.requestId.toString() || "0",
          lastModifiedEmployeeEmailID: userEmail,
          lastModifiedDate: new Date().toISOString(),
          currentStageId: 0,
          Role: currentUserRole,
          fields: partitionedFields.fields,
          efforts:
            hasEffortSection && hasChangedEffortFields
              ? buildEffortPayloadRecords()
              : [],
          savings:
            hasSavingSection && hasChangedSavingFields
              ? buildSavingPayloadRecords()
              : [],
          agentType: "CHAT_AGENT",
        };

        const response = await OrdersService.updateOrder(payload);
        const validationWarnings = getUpdateOrderValidationWarnings(response);

        if (validationWarnings.length > 0) {
          setSubmitError(VALIDATION_WARNINGS_ERROR_MESSAGE);
          setSubmitErrorDetails(validationWarnings);
          return;
        }

        setSubmitSuccess(true);
        setSubmitErrorDetails([]);
        setFieldValidationErrors({}); // Clear validation errors on success

        // Notify parent component of success with response message
        // The parent (Chatbot.jsx) handles refreshing order details and trail logs
        if (onActionClick) {
          onActionClick({
            action: "updateFieldsSuccess",
            fields: fields,
            responseMessage:
              response?.message ||
              response?.Message ||
              "Order updated successfully",
          });
        }
      } catch (error) {
        console.error("Failed to update fields:", error);
        const responseData = getApiErrorResponseData(error);
        const errorMessages = getApiErrorMessages(error);
        const submitErrorMessage =
          errorMessages.length > 0
            ? DEFAULT_UPDATE_FIELDS_ERROR_MESSAGE
            : getApiErrorDisplayMessage(
                error,
                DEFAULT_UPDATE_FIELDS_ERROR_MESSAGE,
              );

        setSubmitError(submitErrorMessage);
        setSubmitErrorDetails(errorMessages);

        // Notify parent component of failure
        if (onActionClick) {
          const normalizedErrorPayload = {
            ...(responseData && typeof responseData === "object"
              ? responseData
              : {}),
            message:
              errorMessages.length > 0
                ? errorMessages.join(" ")
                : submitErrorMessage,
            errors:
              errorMessages.length > 0 ? errorMessages : responseData?.errors,
          };

          onActionClick({
            action: "updateFieldsFailed",
            error: normalizedErrorPayload,
          });
        }
      } finally {
        setIsSubmitting(false);
      }
    } else if (inputValue.trim() && onActionClick) {
      onActionClick({
        action: "updateField",
        fieldName: fieldName,
        newValue: inputValue.trim(),
      });
    }
  };

  const derivedSectionGroups = useMemo(() => {
    if (!isDerivedSectionUiFields) {
      return [];
    }

    const sectionLabels = {
      [EFFORT_SECTION_NAME]: "Efforts",
      [SAVING_SECTION_NAME]: "Savings",
    };

    return [EFFORT_SECTION_NAME, SAVING_SECTION_NAME]

      .map((sectionName) => {
        const sectionFields = visibleResolvedUiFields.filter(
          (field) => field?.meta?.section_name === sectionName,
        );

        if (sectionFields.length === 0) {
          return null;
        }

        const rowsByKey = new Map();

        sectionFields.forEach((field) => {
          const rowKey = field?.meta?.row_key || field.field_name;
          const existingRow = rowsByKey.get(rowKey);

          if (existingRow) {
            existingRow.fields.push(field);
            return;
          }

          rowsByKey.set(rowKey, {
            rowKey,
            rowIndex: field?.meta?.row_index ?? 0,
            fields: [field],
          });
        });

        return {
          sectionName,
          displayName: sectionLabels[sectionName] || sectionName,
          rows: Array.from(rowsByKey.values()).sort(
            (left, right) => left.rowIndex - right.rowIndex,
          ),
        };
      })
      .filter(Boolean);
  }, [isDerivedSectionUiFields, visibleResolvedUiFields]);

  // Render input field based on value_format
  const renderField = (field) => {
    const { field_name, updatedValue, meta } = field;
    const displayLabel =
      field?.display_label || meta?.display_label || field_name;
    const backendFieldKey = meta?.backend_field || field_name;
    const inputType = meta?.input_type || "text";
    const valueFormat =
      inputType === "text"
        ? meta?.value_format?.toLowerCase() || "text"
        : inputType;
    const fieldType = (field.type || meta?.type || "").toString().toLowerCase();
    const disambiguationOptions = Array.isArray(field.options)
      ? field.options
      : Array.isArray(meta?.options)
        ? meta.options
        : [];
    const matchingOrderField = findMatchingOrderField(
      backendFieldKey,
      displayLabel,
      meta?.row_key || "",
    );
    const orderFieldDropdownOptions = getDropdownOptions(matchingOrderField);
    const resolvedSectionName =
      meta?.section_name || matchingOrderField?.sectionName || "";
    const isDisambiguation =
      fieldType === "disambiguation" || disambiguationOptions.length > 0;
    const isDropdown = isDisambiguation || meta?.dropdown || false;
    const dropdownOptions = isDisambiguation
      ? disambiguationOptions
      : meta?.dropdown_options?.length > 0
        ? meta.dropdown_options
        : orderFieldDropdownOptions;
    // When the AI response marks this field as type === "active_directory"
    // (on the field or meta), render the ADO user search regardless of the
    // value_format/input_type driven switch below.

    const isActiveDirectoryField =
      fieldType === "active_directory" ||
      fieldType.toLowerCase() === "activedirectory";
    const value = formValues[field_name] || "";
    const isSavingsApplicationUsedField =
      resolvedSectionName === SAVING_SECTION_NAME &&
      doesFieldMatchCandidates(
        {
          field: backendFieldKey,
          labelToShowOnUI: displayLabel,
        },
        ["ApplicationUsedId", "Application Used"],
      );
    const isEffortForField =
      resolvedSectionName === EFFORT_SECTION_NAME &&
      doesFieldMatchCandidates(
        {
          field: backendFieldKey,
          labelToShowOnUI: displayLabel,
        },
        ["EffortForMasterId", "EffortFor", "Effort For", "Efforts For"],
      );
    const resolvedDropdownValue =
      isDropdown && isSavingsApplicationUsedField
        ? getApplicationUsedDropdownSelectValue(
            { fieldValueList: dropdownOptions },
            value,
          )
        : isDropdown && isEffortForField
          ? getEffortForDropdownSelectValue(
              { fieldValueList: dropdownOptions },
              value,
            )
          : value;

    const selectedDropdownDisplayValue = isDropdown
      ? isSavingsApplicationUsedField
        ? getApplicationUsedDropdownDisplayValue(
            { fieldValueList: dropdownOptions },
            value,
          )
        : isEffortForField
          ? getEffortForDropdownDisplayValue(
              { fieldValueList: dropdownOptions },
              value,
            )
          : getDropdownDisplayMatchValue(
              { fieldValueList: dropdownOptions },
              value,
            )
      : "";
    const fieldValidationError = fieldValidationErrors[field_name];
    const isFieldEditable = isOrderFieldEditable(matchingOrderField);
    const isFieldDisabled = !isFieldEditable || isSubmitting || submitSuccess;

    if (isDropdown) {
      return (
        <div className="ui-field-item" key={field_name}>
          <label className="ui-field-label">{displayLabel}</label>
          <div
            className="ui-field-select-tooltip-target"
            title={selectedDropdownDisplayValue || undefined}
          >
            <MotifSelect
              className="ui-field-select"
              value={resolvedDropdownValue}
              onChange={(e) =>
                handleFieldChange(
                  field_name,
                  e,
                  valueFormat,
                  backendFieldKey,
                  displayLabel,
                )
              }
              disabled={isFieldDisabled}
              placeholder={`Select ${displayLabel}`}
              filter
            >
              {dropdownOptions.length > 0
                ? dropdownOptions.map((option, index) => {
                    const optionValue = getDropdownOptionValue(option);
                    const optionLabel = getDropdownOptionLabel(option);
                    return (
                      <MotifOption
                        key={optionValue || index}
                        value={optionValue}
                        title={optionLabel}
                        disabled={option?.isDisabled}
                      >
                        {optionLabel}
                      </MotifOption>
                    );
                  })
                : updatedValue && (
                    <MotifOption value={updatedValue} title={updatedValue}>
                      {updatedValue}
                    </MotifOption>
                  )}
            </MotifSelect>
          </div>
          {fieldValidationError && !submitSuccess && (
            <span className="ui-field-warning-text">
              {fieldValidationError}
            </span>
          )}
        </div>
      );
    }

    // AI response driven active_directory field type - render ADO search.
    if (isActiveDirectoryField) {
      return (
        <div className="ui-field-item" key={field_name}>
          <label className="ui-field-label">{displayLabel}</label>
          <ActiveDirectoryUserSearch
            value={value}
            onChange={(email) =>
              handleFieldChange(
                field_name,
                email,
                "email",
                backendFieldKey,
                displayLabel,
              )
            }
            disabled={isFieldDisabled}
            placeholder={`Search for ${displayLabel}...`}
            hasError={!!fieldValidationError && !submitSuccess}
            fieldKey={field_name}
          />
          {fieldValidationError && !submitSuccess && (
            <span className="ui-field-warning-text">
              {fieldValidationError}
            </span>
          )}
        </div>
      );
    }

    switch (valueFormat) {
      case "date": {
        const dateDisplayValue = getDateDisplayValue(value);
        const dateInputValue = getDateInputValue(value);

        return (
          <div className="ui-field-item" key={field_name}>
            <label className="ui-field-label">{displayLabel}</label>
            <div
              className={`ui-date-field-wrapper ${isFieldDisabled ? "ui-date-field-wrapper--disabled" : ""}`}
            >
              <input
                type="date"
                className={`ui-field-input ${dateDisplayValue ? "ui-field-input--date-display" : ""}`}
                value={dateInputValue}
                title={dateDisplayValue || undefined}
                onClick={(event) => {
                  try {
                    event.currentTarget.showPicker?.();
                  } catch (error) {}
                }}
                onFocus={(event) => {
                  try {
                    event.currentTarget.showPicker?.();
                  } catch (error) {}
                }}
                onKeyDown={(event) => {
                  if (!["Tab", "Shift", "Escape"].includes(event.key)) {
                    event.preventDefault();
                  }
                }}
                onChange={(e) =>
                  handleFieldChange(
                    field_name,
                    e.target.value,
                    "date",
                    backendFieldKey,
                    displayLabel,
                  )
                }
                disabled={isFieldDisabled}
                inputMode="none"
              />
              {dateDisplayValue && (
                <span className="ui-date-display-value" aria-hidden="true">
                  {dateDisplayValue}
                </span>
              )}
            </div>
            {fieldValidationError && !submitSuccess && (
              <span className="ui-field-warning-text">
                {fieldValidationError}
              </span>
            )}
          </div>
        );
      }
      case "number":
        return (
          <div className="ui-field-item" key={field_name}>
            <label className="ui-field-label">{displayLabel}</label>
            <input
              type="number"
              className="ui-field-input"
              value={value}
              title={getInputTooltipTitle(value)}
              onChange={(e) =>
                handleFieldChange(
                  field_name,
                  e.target.value,
                  "number",
                  backendFieldKey,
                  displayLabel,
                )
              }
              placeholder={`Enter ${displayLabel}`}
              disabled={isFieldDisabled}
            />
            {fieldValidationError && !submitSuccess && (
              <span className="ui-field-warning-text">
                {fieldValidationError}
              </span>
            )}
          </div>
        );
      case "datetime":
        return (
          <div className="ui-field-item" key={field_name}>
            <label className="ui-field-label">{displayLabel}</label>
            <input
              type="datetime-local"
              className="ui-field-input"
              value={value}
              title={getInputTooltipTitle(value)}
              min={nowMinDateTime}
              onChange={(e) =>
                handleFieldChange(
                  field_name,
                  e.target.value,
                  "datetime",
                  backendFieldKey,
                  displayLabel,
                )
              }
              disabled={isFieldDisabled}
            />
            {fieldValidationError && !submitSuccess && (
              <span className="ui-field-warning-text">
                {fieldValidationError}
              </span>
            )}
          </div>
        );
      case "email":
        return (
          <div className="ui-field-item" key={field_name}>
            <label className="ui-field-label">{displayLabel}</label>
            <input
              type="email"
              className="ui-field-input"
              value={value}
              title={getInputTooltipTitle(value)}
              onChange={(e) =>
                handleFieldChange(
                  field_name,
                  e.target.value,
                  "email",
                  backendFieldKey,
                  displayLabel,
                )
              }
              placeholder={`Enter ${displayLabel}`}
              disabled={isFieldDisabled}
            />
            {fieldValidationError && !submitSuccess && (
              <span className="ui-field-warning-text">
                {fieldValidationError}
              </span>
            )}
          </div>
        );
      case "active_directory":
        return (
          <div className="ui-field-item" key={field_name}>
            <label className="ui-field-label">{displayLabel}</label>
            <ActiveDirectoryUserSearch
              value={value}
              onChange={(email) =>
                handleFieldChange(
                  field_name,
                  email,
                  "email",
                  backendFieldKey,
                  displayLabel,
                )
              }
              disabled={isFieldDisabled}
              placeholder={`Search for ${displayLabel}...`}
              hasError={!!fieldValidationError && !submitSuccess}
              fieldKey={field_name}
            />
            {fieldValidationError && !submitSuccess && (
              <span className="ui-field-warning-text">
                {fieldValidationError}
              </span>
            )}
          </div>
        );
      case "textarea":
        return (
          <div className="ui-field-item ui-field-item--full" key={field_name}>
            <label className="ui-field-label">{displayLabel}</label>
            <textarea
              className="ui-field-textarea"
              value={value}
              title={getInputTooltipTitle(value)}
              onChange={(e) =>
                handleFieldChange(
                  field_name,
                  e.target.value,
                  "textarea",
                  backendFieldKey,
                  displayLabel,
                )
              }
              placeholder={`Enter ${displayLabel}`}
              rows={3}
              disabled={isFieldDisabled}
            />
            {fieldValidationError && !submitSuccess && (
              <span className="ui-field-warning-text">
                {fieldValidationError}
              </span>
            )}
          </div>
        );
      case "text":
      default:
        return (
          <div className="ui-field-item" key={field_name}>
            <label className="ui-field-label">{displayLabel}</label>
            <input
              type="text"
              className="ui-field-input"
              value={value}
              title={getInputTooltipTitle(value)}
              onChange={(e) =>
                handleFieldChange(
                  field_name,
                  e.target.value,
                  "text",
                  backendFieldKey,
                  displayLabel,
                )
              }
              placeholder={`Enter ${displayLabel}`}
              disabled={isFieldDisabled}
            />
            {fieldValidationError && !submitSuccess && (
              <span className="ui-field-warning-text">
                {fieldValidationError}
              </span>
            )}
          </div>
        );
    }
  };

  // Render UI fields in rows of 2
  const renderUIFields = () => {
    if (!visibleResolvedUiFields || visibleResolvedUiFields.length === 0) {
      return null;
    }

    return (
      <div className="ui-fields-container">
        {derivedSectionGroups.length > 0 ? (
          <div className="ui-fields-sections">
            {/* <>*/}
            {derivedSectionGroups.map((sectionGroup) => (
              <div
                className="ui-fields-section-group"
                key={sectionGroup.sectionName}
              >
                <div className="ui-fields-section-title">
                  {sectionGroup.displayName}
                </div>
                {sectionGroup.rows.map((row, rowIndex) => (
                  <div className="ui-fields-section-row" key={row.rowKey}>
                    {sectionGroup.rows.length > 1 && (
                      <div className="ui-fields-section-row-title">
                        {`${sectionGroup.displayName} ${rowIndex + 1}`}
                      </div>
                    )}
                    <div className="ui-fields-grid">
                      {row.fields.map((field) => renderField(field))}
                    </div>
                  </div>
                ))}
                {/*    </>*/}
              </div>
            ))}
          </div>
        ) : (
          <div className="ui-fields-grid">
            {visibleResolvedUiFields.map((field) => renderField(field))}
          </div>
        )}

        {/* Error message */}
        {submitError && (
          <div className="ui-fields-error">
            <span className="ui-fields-error-icon">⚠️</span>
            <div className="ui-fields-error-content">
              <span className="ui-fields-error-text">{submitError}</span>
              {submitErrorDetails.length > 0 && (
                <ul className="ui-fields-error-list">
                  {submitErrorDetails.map((warning, index) => (
                    <li key={`${warning}-${index}`}>{warning}</li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        {/* Success message - hidden for checkpoint resume flow since the AI
            follow-up response is rendered as the next chat bubble. */}
        {submitSuccess && !message.checkpoint_pending && (
          <div className="ui-fields-success">
            <span className="ui-fields-success-icon">✓</span>
            <span className="ui-fields-success-text">
              Fields updated successfully!
            </span>
          </div>
        )}

        <div
          className={`ui-fields-actions${
            derivedSectionGroups.length > 0
              ? " ui-fields-actions--with-sections"
              : ""
          }`}
        >
          <button
            className="ui-fields-submit-btn"
            onClick={handleSubmit}
            disabled={isSubmitting || submitSuccess || hasFieldValidationErrors}
          >
            {message.checkpoint_pending
              ? isSubmitting
                ? "Confirming..."
                : submitSuccess
                  ? "Confirmed"
                  : "Confirm"
              : isSubmitting
                ? "Updating..."
                : submitSuccess
                  ? "Updated"
                  : "Update Fields"}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className={`order-chat-bubble ${message.role}`}>
      <div className="order-ai-card update-single-field-card">
        <div className="order-ai-card-header">
          <div className="order-ai-card-title-logo">
            <img
              src={aiChatlogo}
              alt="LOGO"
              aria-hidden="true"
              className="order-ai-logo"
              style={{ width: 32, height: 32 }}
            />
          </div>
          <div className="order-ai-card-title-Avatar">
            <span>{message.header?.title || "Axon AI"}</span>
            <div className="order-ai-card-time">{`${day} ${time}`}</div>
          </div>
        </div>

        <div className="order-ai-card-body">
          {/* Reallocation agent disclaimer */}
          {/* {message.checkpoint_pending &&
            message.checkpoint_pending_agent === "reallocation_agent" && (
              <div className="reallocation-disclaimer">
                <InfoCircleIcon width="16" height="16" color="#A4A3B1" />
                <span>
                  When adding a team member, regardless of role, remind them to
                  comply with local independence policies before accessing a
                  solution or agent, and complete the required independence
                  declaration in EY Canvas (or equivalent, if applicable).
                </span>
              </div>
            )} */}

          {/* Show UI Fields form when ui_fields_flag is true */}
          {shouldRenderUiFields ? (
            <>
              <div className="update-single-field-header markdown-content">
                <SimpleMarkdown>
                  {message.text ||
                    "Please review and update the following fields:"}
                </SimpleMarkdown>
              </div>
              {renderUIFields()}
            </>
          ) : (
            <>
              <div className="update-single-field-header">
                {currentValueMembers.length > 0 ? (
                  <div className="update-single-field-current-value">
                    <div className="update-single-field-current-label">
                      {fieldName}
                    </div>
                    <div
                      className={`update-single-field-current-content ${
                        currentValueMembers.length > 1 ? "is-multiline" : ""
                      }`.trim()}
                    >
                      {currentValueMembers.length > 1
                        ? currentValueMembers.map((member) => (
                            <div key={member}>{member}</div>
                          ))
                        : currentValueMembers[0]}
                    </div>
                    <p className="update-single-field-text">
                      What value do you want to replace with?
                    </p>
                  </div>
                ) : (
                  <p className="update-single-field-text">
                    Current value for {fieldName} is {currentValue}. What Value
                    you want to replace with?
                  </p>
                )}
              </div>

              <div className="update-single-field-instruction">
                <p className="update-single-field-instruction-text">
                  Please Provide updated value below.
                </p>
              </div>

              <div className="update-single-field-validation-info">
                <span className="validation-icon">
                  <Iconi />
                </span>
                <div className="validation-text">
                  The value you put is subject to backend validation. Follow
                  below help guide to prevent errors.
                </div>
              </div>

              <div className="update-single-field-help">
                <ul className="help-list">
                  <li>
                    Make sure the value is in Numeric form (Only numbers are
                    accepted).
                  </li>
                </ul>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
